import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import config, { getSubdomainUrl } from "../../config";
import { projectActions } from "../../store/project-store";
import { cuteToast } from "../UI/cute";
import {
  getSampleData,
  renderComponentDynamic,
  getHtmlTemplate,
  generateCSSVariables
} from "../../data/component-templates/index";
import { getThemeStyleGuide } from "../../data/component-templates/themes";
import {
  FiArrowLeft,
  FiMonitor,
  FiTablet,
  FiSmartphone,
  FiEye,
  FiZap,
  FiChevronRight,
  FiChevronDown,
  FiMenu,
  FiChevronUp,
  FiEyeOff,
  FiTrash2,
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiX,
  FiSave,
  FiSettings,
  FiRefreshCw,
  FiMap,
  FiGlobe,
  FiCheckCircle,
  FiMail,
  FiUpload,
  FiCode,
  FiLink,
} from "react-icons/fi";
import "./WireframeEditor.css";
import ImageUploadModal from "./ImageUploadModal";
import MapEditModal from "./MapEditModal";
import PublishModal from "./PublishModal";
import IconPickerModal from "./IconPickerModal";
import ConfirmModal from "./ConfirmModal";

const WireframeEditor = () => {
  const { project_id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { sendRequest } = useHttp();
  const canvasRef = useRef(null);

  const { currentProject, wireframes, styleGuide } = useSelector((state) => state.project);
  const authToken = useSelector((state) => state.auth.token);

  const [isLoading, setIsLoading] = useState(true);
  const [zoom, setZoom] = useState(70); // 70% to fit 1440px viewport nicely
  const [deviceMode, setDeviceMode] = useState("desktop"); // desktop, tablet, mobile
  const [expandedPages, setExpandedPages] = useState({ 0: true });
  const [selectedSection, setSelectedSection] = useState(null);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [currentPageId, setCurrentPageId] = useState(null); // Track currently displayed page
  const [showStyleGuide, setShowStyleGuide] = useState(true);
  const [styleGuidePosition, setStyleGuidePosition] = useState({ x: window.innerWidth - 320, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Inline editing state
  const [modifiedSections, setModifiedSections] = useState({}); // { wireframeId: htmlContent }
  const modifiedSectionsRef = useRef({}); // Silent ref to track inline edits without re-render
  const [editCount, setEditCount] = useState(0); // Lightweight counter to trigger save button re-render
  const [isSaving, setIsSaving] = useState(false);
  const sectionRefs = useRef({}); // Store refs for each section's DOM

  // Link/Button editing popup state
  const [linkPopup, setLinkPopup] = useState({
    show: false,
    element: null,
    text: '',
    href: '',
    linkType: 'page', // 'page' or 'custom'
    isIconOnly: false, // true when link has only icon, no text (e.g., social icons)
    iconElement: null, // reference to the <i> element inside the link
    wireframeId: null,
    container: null,
    position: { x: 0, y: 0 }
  });

  // Image editing modal state
  const [imageModal, setImageModal] = useState({
    show: false,
    element: null,
    currentSrc: '',
    isBackground: false,
    wireframeId: null,
    container: null
  });

  // Map editing modal state
  const [mapModal, setMapModal] = useState({
    show: false,
    mapContainer: null,
    addressElement: null,
    currentAddress: '',
    wireframeId: null,
    container: null
  });

  // Icon picker modal state
  const [iconPickerModal, setIconPickerModal] = useState({
    show: false,
    currentIcon: '',
    iconElement: null,
    wireframeId: null,
    container: null,
  });

  // Input placeholder editing popup state
  const [placeholderPopup, setPlaceholderPopup] = useState({
    show: false,
    element: null,
    placeholder: '',
    wireframeId: null,
    container: null,
    position: { x: 0, y: 0 }
  });

  // AI Regeneration state
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [regenPrompt, setRegenPrompt] = useState({ show: false, section: null, instructions: '' });

  // Publish modal state
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Leave confirmation modal state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // SEO panel state
  const [showFaviconModal, setShowFaviconModal] = useState(false);
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [seoPosition, setSeoPosition] = useState({ x: window.innerWidth - 640, y: 80 });
  const [seoDragging, setSeoDragging] = useState(false);
  const [seoDragOffset, setSeoDragOffset] = useState({ x: 0, y: 0 });
  const [seoSettings, setSeoSettings] = useState({ site_title: '', meta_description: '', meta_keywords: '', favicon_url: '' });
  const [seoSaving, setSeoSaving] = useState(false);
  const [seoLoaded, setSeoLoaded] = useState(false);

  // Autoresponder panel state
  const [showAutoresponder, setShowAutoresponder] = useState(false);
  const [arPosition, setArPosition] = useState({ x: window.innerWidth - 640, y: 80 });
  const [arDragging, setArDragging] = useState(false);
  const [arDragOffset, setArDragOffset] = useState({ x: 0, y: 0 });
  const [arConfig, setArConfig] = useState({ provider: '', list_id: '', list_name: '' });
  const [arProviders, setArProviders] = useState([]);
  const [arLists, setArLists] = useState([]);
  const [arLoadingLists, setArLoadingLists] = useState(false);
  const [arSaving, setArSaving] = useState(false);
  const [arLoaded, setArLoaded] = useState(false);

  // Custom Code panel state
  const [showCustomCodePanel, setShowCustomCodePanel] = useState(false);
  const [customCodePosition, setCustomCodePosition] = useState({ x: window.innerWidth - 640, y: 80 });
  const [customCodeDragging, setCustomCodeDragging] = useState(false);
  const [customCodeDragOffset, setCustomCodeDragOffset] = useState({ x: 0, y: 0 });
  const [customCodeData, setCustomCodeData] = useState({ custom_css: '', custom_script: '' });
  const [customCodeSaving, setCustomCodeSaving] = useState(false);
  const [customCodeLoaded, setCustomCodeLoaded] = useState(false);
  const [customCodeTab, setCustomCodeTab] = useState('css');

  // CNAME / Custom Domain panel state
  const [showCnamePanel, setShowCnamePanel] = useState(false);
  const [cnamePosition, setCnamePosition] = useState({ x: window.innerWidth - 640, y: 80 });
  const [cnameDragging, setCnameDragging] = useState(false);
  const [cnameDragOffset, setCnameDragOffset] = useState({ x: 0, y: 0 });
  const [cnameData, setCnameData] = useState({ custom_domain: '', subdomain: '', project_status: '' });
  const [cnameInput, setCnameInput] = useState('');
  const [cnameSaving, setCnameSaving] = useState(false);
  const [cnameLoaded, setCnameLoaded] = useState(false);
  const [cnameRemoving, setCnameRemoving] = useState(false);

  // AI Style Suggestion modal state
  const [showAiStyleModal, setShowAiStyleModal] = useState(false);
  const [aiStyleLoading, setAiStyleLoading] = useState(false);
  const [aiStyleMood, setAiStyleMood] = useState('Modern and clean');
  const [aiStyleCustom, setAiStyleCustom] = useState('');
  const [aiStyleImageUrl, setAiStyleImageUrl] = useState('');
  const [aiStyleImageUploading, setAiStyleImageUploading] = useState(false);
  const [aiStyleSuggestions, setAiStyleSuggestions] = useState([]);

  // Use theme-aware default style guide
  const themeStyleGuide = getThemeStyleGuide(currentProject?.theme || 'classic');
  const defaultStyleGuide = {
    colors: themeStyleGuide?.colors || { primary: "#059669", secondary: "#d97706", background: "#fafdf7", text: "#0c1f17", muted: "#5f7367" },
    fonts: {
      heading: themeStyleGuide?.typography?.heading?.font_family || "Outfit",
      body: themeStyleGuide?.typography?.body?.font_family || "DM Sans",
    },
    buttons: {
      borderRadius: themeStyleGuide?.ui?.button?.border_radius || "10px",
      padding: themeStyleGuide?.ui?.button?.padding || "14px 28px",
    },
  };

  // Normalize styleGuide from Redux (which has {colors, typography, ui} format)
  // to editor format {colors, fonts, buttons}
  const normalizedStyleGuide = styleGuide ? {
    colors: styleGuide.colors || defaultStyleGuide.colors,
    fonts: styleGuide.fonts || (styleGuide.typography ? {
      heading: styleGuide.typography.heading?.font_family || defaultStyleGuide.fonts.heading,
      body: styleGuide.typography.body?.font_family || defaultStyleGuide.fonts.body,
    } : defaultStyleGuide.fonts),
    buttons: styleGuide.buttons || (styleGuide.ui ? {
      borderRadius: styleGuide.ui.button?.border_radius || defaultStyleGuide.buttons.borderRadius,
      padding: styleGuide.ui.button?.padding || defaultStyleGuide.buttons.padding,
    } : defaultStyleGuide.buttons),
  } : null;

  const currentStyle = normalizedStyleGuide || defaultStyleGuide;

  useEffect(() => {
    document.title = `${endPoints.title} | Editor`;
    loadProject();
  }, [project_id]);

  // Keyboard shortcuts for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Plus (=) for zoom in
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      }
      // Ctrl/Cmd + Minus for zoom out
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      // Ctrl/Cmd + 0 for reset zoom
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleFitScreen();
      }
    };

    const handleWheel = (e) => {
      // Ctrl/Cmd + Mouse Wheel for zoom
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        // deltaY < 0 means scrolling up (zoom in)
        // deltaY > 0 means scrolling down (zoom out)
        if (e.deltaY < 0) {
          handleZoomIn();
        } else if (e.deltaY > 0) {
          handleZoomOut();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [zoom]); // Include zoom in dependencies so handlers have latest value

  // Warn user before leaving page with unsaved changes (browser refresh / tab close)
  // Note: browser enforces its own native dialog for this — cannot be customized
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (Object.keys(modifiedSections).length > 0 || Object.keys(modifiedSectionsRef.current).length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modifiedSections]);

  // Set initial page when wireframes load
  useEffect(() => {
    if (wireframes && wireframes.length > 0 && !currentPageId) {
      const pages = groupWireframesByPage();
      if (pages.length > 0) {
        setCurrentPageId(pages[0].page_id);
      }
    }
  }, [wireframes]);

  // Setup inline editing when sections are rendered
  useEffect(() => {
    if (!wireframes || wireframes.length === 0 || !currentPageId) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Get page-specific sections AND global sections (header/footer)
      const currentSections = wireframes.filter(w =>
        w.page_id === currentPageId || w.is_global || w.page_id === '_global'
      );
      currentSections.forEach(section => {
        const container = document.querySelector(`[data-section-id="${section.id}"]`);
        if (container) {
          setupInlineEditing(container, section.id);
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [wireframes, currentPageId, currentStyle]);

  // Re-attach inline editing handlers after a section's HTML is updated in state.
  // When modifiedSections changes, React re-renders the section with dangerouslySetInnerHTML
  // which replaces the DOM and destroys all event listeners — so we must re-run setup.
  useEffect(() => {
    const ids = Object.keys(modifiedSections);
    if (ids.length === 0) return;
    const timer = setTimeout(() => {
      ids.forEach(id => {
        const container = document.querySelector(`[data-section-id="${id}"]`);
        if (container) setupInlineEditing(container, parseInt(id));
      });
    }, 30);
    return () => clearTimeout(timer);
  }, [modifiedSections]);

  const loadProject = () => {
    setIsLoading(true);
    sendRequest(
      { url: `${endPoints.projects}/${project_id}`, method: "GET" },
      (response) => {
        setIsLoading(false);
        if (response.status === 200) {
          dispatch(projectActions.setCurrentProject(response.body));
          dispatch(projectActions.setWireframes(response.body.wireframes || []));

          if (response.body.style_guide_json) {
            try {
              dispatch(projectActions.setStyleGuide(JSON.parse(response.body.style_guide_json)));
            } catch (e) {
              dispatch(projectActions.setStyleGuide(defaultStyleGuide));
            }
          } else {
            dispatch(projectActions.setStyleGuide(defaultStyleGuide));
          }
        }
      }
    );
  };

  const handleSaveStyleGuide = () => {
    // Save in both editor format (colors, fonts, buttons) and
    // published site format (colors, typography, ui) for compatibility
    const themeDefaults = themeStyleGuide || {};
    const fullStyleGuide = {
      ...currentStyle,
      typography: {
        heading: {
          font_family: currentStyle.fonts.heading,
          font_weight: themeDefaults.typography?.heading?.font_weight || 700
        },
        body: {
          font_family: currentStyle.fonts.body,
          font_weight: themeDefaults.typography?.body?.font_weight || 400
        }
      },
      ui: {
        button: {
          border_radius: currentStyle.buttons.borderRadius,
          padding: currentStyle.buttons.padding || themeDefaults.ui?.button?.padding || "14px 28px",
          text_color: themeDefaults.ui?.button?.text_color || "#ffffff",
          font_weight: themeDefaults.ui?.button?.font_weight || "600",
          box_shadow: themeDefaults.ui?.button?.box_shadow || "none"
        },
        input: {
          ...(themeDefaults.ui?.input || {}),
          focus_border: currentStyle.colors.primary || themeDefaults.ui?.input?.focus_border || "#059669"
        }
      }
    };
    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/style-guide`,
        method: "POST",
        body: { style_guide_json: JSON.stringify(fullStyleGuide) },
      },
      (response) => {
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Saved",
            message: "Style guide updated",
            timer: 2000,
          });
        }
      }
    );
  };

  const handleColorChange = (colorKey, value) => {
    const newStyleGuide = {
      ...currentStyle,
      colors: { ...currentStyle.colors, [colorKey]: value },
    };
    dispatch(projectActions.setStyleGuide(newStyleGuide));
  };

  const handleFontChange = (fontKey, value) => {
    const newStyleGuide = {
      ...currentStyle,
      fonts: { ...currentStyle.fonts, [fontKey]: value },
    };
    dispatch(projectActions.setStyleGuide(newStyleGuide));
  };

  const handleButtonChange = (buttonKey, value) => {
    const newStyleGuide = {
      ...currentStyle,
      buttons: { ...currentStyle.buttons, [buttonKey]: value },
    };
    dispatch(projectActions.setStyleGuide(newStyleGuide));
  };

  const handleGenerateAiStyle = () => {
    setAiStyleLoading(true);
    setAiStyleSuggestions([]);
    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/suggest-style`,
        method: "POST",
        body: {
          mood: aiStyleMood,
          custom_instructions: aiStyleCustom,
          reference_image_url: aiStyleImageUrl,
        },
      },
      (response) => {
        setAiStyleLoading(false);
        if (response.status === 200) {
          setAiStyleSuggestions(response.body || []);
        } else {
          cuteToast({ type: "error", title: "Error", message: response.message || "Failed to generate suggestions", timer: 4000 });
        }
      }
    );
  };

  const handleApplyAiScheme = (scheme) => {
    const newStyleGuide = {
      colors: { ...scheme.colors },
      fonts: { heading: scheme.fonts?.heading || currentStyle.fonts.heading, body: scheme.fonts?.body || currentStyle.fonts.body },
      buttons: { ...currentStyle.buttons, borderRadius: scheme.buttons?.borderRadius || currentStyle.buttons.borderRadius },
    };
    dispatch(projectActions.setStyleGuide(newStyleGuide));
    setShowAiStyleModal(false);
    setAiStyleSuggestions([]);
    cuteToast({ type: "success", title: "Applied", message: `"${scheme.name}" applied. Click Save to keep changes.`, timer: 3000 });
  };

  const handleAiStyleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAiStyleImageUploading(true);
    const formData = new FormData();
    formData.append("image", file);
    fetch(endPoints.mediaUpload, {
      method: "POST",
      headers: { Authorization: "Bearer " + authToken },
      body: formData,
    })
      .then((r) => r.json())
      .then((data) => {
        setAiStyleImageUploading(false);
        if (data.status === 200 && data.image?.url) {
          setAiStyleImageUrl(data.image.url);
          cuteToast({ type: "success", title: "Uploaded", message: "Reference image ready", timer: 2000 });
        }
      })
      .catch(() => { setAiStyleImageUploading(false); });
  };

  const handleMoveSection = (direction, section) => {
    console.log('Move section:', direction, section);

    const currentPageSections = wireframes
      .filter(w => w.page_id === section.page_id)
      .sort((a, b) => (a.section_order || 0) - (b.section_order || 0));
    const currentIndex = currentPageSections.findIndex(s => s.id === section.id);

    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === currentPageSections.length - 1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    // Create new objects with swapped order values (don't mutate Redux state)
    const currentSection = currentPageSections[currentIndex];
    const targetSection = currentPageSections[targetIndex];

    const tempOrder = currentSection.section_order || currentIndex;
    const newCurrentOrder = targetSection.section_order || targetIndex;
    const newTargetOrder = tempOrder;

    console.log('Reordering:', { currentSection, targetSection, newCurrentOrder, newTargetOrder });

    // Save original wireframes for revert
    const originalWireframes = wireframes;

    // Update in Redux with new objects
    const updatedWireframes = wireframes.map(w => {
      if (w.id === currentSection.id) {
        return { ...w, section_order: newCurrentOrder };
      }
      if (w.id === targetSection.id) {
        return { ...w, section_order: newTargetOrder };
      }
      return w;
    });

    dispatch(projectActions.setWireframes(updatedWireframes));

    // Save to backend
    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/wireframes/reorder`,
        method: "POST",
        body: {
          wireframes: [
            { id: currentSection.id, section_order: newCurrentOrder, page_id: currentSection.page_id },
            { id: targetSection.id, section_order: newTargetOrder, page_id: targetSection.page_id }
          ]
        },
      },
      (response) => {
        console.log('Reorder response:', response);
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Reordered",
            message: "Section order updated",
            timer: 1500,
          });
          // Reload to get properly sorted sections from backend
          loadProject();
        } else {
          // Revert on error
          console.error('Reorder failed:', response);
          dispatch(projectActions.setWireframes(originalWireframes));
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to reorder sections",
            timer: 3000,
          });
        }
      }
    );
  };

  const handleDeleteSection = (section) => {
    console.log('Delete section:', section);
    if (!window.confirm(`Delete "${section.component_code}" section?`)) return;

    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/wireframes/delete`,
        method: "POST",
        body: { wireframe_id: section.id },
      },
      (response) => {
        console.log('Delete response:', response);
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Deleted",
            message: "Section deleted successfully",
            timer: 2000,
          });
          loadProject();
        } else {
          console.error('Delete failed:', response);
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to delete section",
            timer: 3000,
          });
        }
      }
    );
  };

  const handleToggleVisibility = (section) => {
    console.log('Toggle visibility:', section);
    // Backend uses is_visible (opposite of is_hidden)
    const newVisibility = !section.is_visible;
    console.log('New visibility:', newVisibility);

    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/wireframes/update`,
        method: "POST",
        body: {
          wireframe_id: section.id,
          is_visible: newVisibility,
        },
      },
      (response) => {
        console.log('Visibility toggle response:', response);
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: newVisibility ? "Visible" : "Hidden",
            message: `Section ${newVisibility ? 'shown' : 'hidden'}`,
            timer: 1500,
          });
          loadProject();
        } else {
          console.error('Visibility toggle failed:', response);
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to update visibility",
            timer: 3000,
          });
        }
      }
    );
  };

  // Open the regenerate prompt modal
  const handleOpenRegenPrompt = (section) => {
    if (regeneratingSection) return;
    setRegenPrompt({ show: true, section, instructions: '' });
  };

  // Handle AI regeneration of section HTML (preview only, not saved to DB)
  const handleRegenerateSection = (section, userInstructions) => {
    if (regeneratingSection) return; // Prevent multiple regenerations

    setRegeneratingSection(section.id);
    setRegenPrompt({ show: false, section: null, instructions: '' });

    sendRequest(
      {
        url: `${endPoints.projects}/regenerate-component-html`,
        method: "POST",
        body: { wireframe_id: section.id, user_instructions: userInstructions || '' },
      },
      (response) => {
        setRegeneratingSection(null);

        if (response.status === 200) {
          // Update the modified sections with new HTML (preview only)
          if (response.body?.html_content) {
            setModifiedSections(prev => ({
              ...prev,
              [section.id]: response.body.html_content
            }));
            cuteToast({
              type: "success",
              title: "Preview Ready",
              message: "AI generated new HTML. Click Save to keep changes.",
              timer: 3000,
            });
          }
          // Don't reload project - let user preview and decide to save
        } else {
          cuteToast({
            type: "error",
            title: "Error",
            message: response.message || "Failed to regenerate section",
            timer: 3000,
          });
        }
      }
    );
  };

  const handleResetToDefault = (section, key) => {
    if (section === 'color') {
      handleColorChange(key, defaultStyleGuide.colors[key]);
    } else if (section === 'font') {
      handleFontChange(key, defaultStyleGuide.fonts[key]);
    } else if (section === 'button') {
      handleButtonChange(key, defaultStyleGuide.buttons[key]);
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - styleGuidePosition.x,
      y: e.clientY - styleGuidePosition.y,
    });
  };

  const handleDragMove = (e) => {
    if (isDragging) {
      setStyleGuidePosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset]);

  const togglePage = (pageIndex) => {
    setExpandedPages((prev) => {
      const isCurrentlyExpanded = prev[pageIndex];

      // Accordion behavior: close all other pages
      // If clicking already expanded page, keep it expanded (don't collapse)
      if (isCurrentlyExpanded) {
        return { [pageIndex]: true }; // Keep it open
      } else {
        return { [pageIndex]: true }; // Open this one, close all others
      }
    });
  };

  const handlePageSelect = (pageId) => {
    setCurrentPageId(pageId);
  };

  // Scroll the canvas to bring a section into view
  const scrollToSection = (sectionId) => {
    const sectionEl = canvasRef.current?.querySelector(`[data-section-id="${sectionId}"]`);
    if (sectionEl) {
      sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setEditingSection(section);

    // Parse content for editing
    let parsedContent = null;
    if (section.content_json) {
      try {
        parsedContent = JSON.parse(section.content_json);
      } catch (e) {
        parsedContent = null;
      }
    }

    // Generate default content if none exists - use sample_data_json from component templates
    if (!parsedContent) {
      // Try to get sample data from JSON template
      parsedContent = getSampleData(section.component_code);

      // Fallback for unknown components
      if (!parsedContent) {
        parsedContent = {
          heading: section.component_code.replace(/_/g, " "),
          description: section.purpose || "Section content"
        };
      }
    }

    setEditingContent(parsedContent || {});
  };

  const handleContentChange = (field, value) => {
    setEditingContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveContent = () => {
    if (!editingSection) return;

    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/wireframes/update`,
        method: "POST",
        body: {
          wireframe_id: editingSection.id,
          content_json: JSON.stringify(editingContent),
        },
      },
      (response) => {
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Saved",
            message: "Section updated successfully",
            timer: 2000,
          });
          loadProject();
          setEditingSection(null);
          setEditingContent(null);
        }
      }
    );
  };

  // ============== INLINE EDITING FUNCTIONS ==============

  // Check if element has only icon children (i, svg, img) - no other element types
  const hasOnlyIconChildren = (element) => {
    const children = Array.from(element.children);
    if (children.length === 0) return false;

    const iconTags = ['I', 'SVG', 'IMG', 'SPAN'];
    return children.every(child => {
      // Check if it's an icon tag or a span with icon class
      if (iconTags.includes(child.tagName)) {
        // For spans, check if it looks like an icon wrapper
        if (child.tagName === 'SPAN') {
          return child.children.length > 0 && hasOnlyIconChildren(child);
        }
        return true;
      }
      return false;
    });
  };

  // Get direct text content of element (excluding child element text)
  const getDirectTextContent = (element) => {
    let text = '';
    element.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      }
    });
    return text.trim();
  };

  // Check if element is editable (has text content, may have icon children)
  const isEditableElement = (element) => {
    if (!element) return false;

    // Skip certain elements - but NOT buttons and links (handled separately)
    const skipTags = ['SCRIPT', 'STYLE', 'I', 'SVG', 'IMG', 'INPUT', 'TEXTAREA'];
    if (skipTags.includes(element.tagName)) return false;

    // Check for text content
    const hasTextContent = element.textContent && element.textContent.trim().length > 0;
    if (!hasTextContent) return false;

    // Check if element has child elements
    const children = Array.from(element.children);

    // If no children, it's a pure text element - editable
    if (children.length === 0) return true;

    // If all children are inline formatting tags (em, strong, br, span, etc.) — still editable
    const inlineTags = ['EM', 'STRONG', 'B', 'U', 'MARK', 'SUB', 'SUP', 'BR', 'SMALL', 'SPAN'];
    const allInline = children.every(child => inlineTags.includes(child.tagName));
    if (allInline) return true;

    // If has children, check if they're only icons (i, svg, img)
    // This allows editing of elements like: <li><i class="icon"></i> Some text</li>
    if (hasOnlyIconChildren(element)) {
      // Make sure there's actual text besides the icons
      const directText = getDirectTextContent(element);
      return directText.length > 0;
    }

    return false;
  };

  // Check if element is a link or button
  const isLinkOrButton = (element) => {
    if (!element) return false;
    return element.tagName === 'A' || element.tagName === 'BUTTON';
  };

  // Get unique page routes from wireframes for link dropdown
  const getPageRoutes = () => {
    if (!wireframes || wireframes.length === 0) return [];
    const routeMap = new Map();
    wireframes.forEach(wf => {
      if (wf.page_route && wf.page_name && !wf.is_global) {
        routeMap.set(wf.page_route, wf.page_name);
      }
    });
    return Array.from(routeMap, ([route, name]) => ({ route, name }));
  };

  // Check if href is a page route or custom link
  const isPageRoute = (href) => {
    if (!href) return true;
    const pageRoutes = getPageRoutes();
    return pageRoutes.some(p => p.route === href) || href === '/' || href === '#';
  };

  // Validate custom URL
  const isValidCustomUrl = (url) => {
    if (!url) return false;
    // Allow hash links
    if (url.startsWith('#')) return true;
    // Must start with http:// or https://
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Handle link/button click - show popup
  const handleLinkButtonClick = (e, element, wireframeId, container) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = element.getBoundingClientRect();
    const text = element.textContent?.trim() || '';
    const href = element.getAttribute('href') || '';

    // Determine if it's a page route or custom link
    const linkType = isPageRoute(href) ? 'page' : 'custom';

    // Detect icon-only links (e.g., social media icons) — has icon but no meaningful text
    const icons = element.querySelectorAll('i[class*="bi-"]');
    const directText = getDirectTextContent(element);
    const isIconOnly = icons.length > 0 && !directText;

    setLinkPopup({
      show: true,
      element: element,
      text: text,
      href: href,
      linkType: linkType,
      isIconOnly: isIconOnly,
      iconElement: isIconOnly ? icons[0] : null,
      wireframeId: wireframeId,
      container: container,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.bottom + 10
      }
    });
  };

  // Save link/button changes
  const handleSaveLinkPopup = () => {
    // Validate custom URL if custom type is selected
    if (linkPopup.linkType === 'custom' && linkPopup.href) {
      if (!isValidCustomUrl(linkPopup.href)) {
        alert('Please enter a valid URL starting with http:// or https://, or use # for anchor links.');
        return;
      }
    }

    if (linkPopup.element) {
      // For icon-only links (social icons), don't update text — only update href
      if (!linkPopup.isIconOnly) {
        // Update the element text (preserve icons if any)
        const icons = linkPopup.element.querySelectorAll('i, svg');
        if (icons.length > 0) {
          // Has icons - need to preserve them
          const iconHtml = Array.from(icons).map(icon => icon.outerHTML).join('');
          linkPopup.element.innerHTML = iconHtml + ' ' + linkPopup.text;
        } else {
          linkPopup.element.textContent = linkPopup.text;
        }
      }

      // Update href if it's a link
      if (linkPopup.element.tagName === 'A') {
        linkPopup.element.setAttribute('href', linkPopup.href || '#');
      }

      // Mark section as modified
      markSectionModified(linkPopup.wireframeId, linkPopup.container);
    }

    // Close popup
    setLinkPopup({
      show: false,
      element: null,
      text: '',
      href: '',
      linkType: 'page',
      isIconOnly: false,
      iconElement: null,
      wireframeId: null,
      container: null,
      position: { x: 0, y: 0 }
    });
  };

  // Close link popup without saving
  const handleCloseLinkPopup = () => {
    setLinkPopup({
      show: false,
      element: null,
      text: '',
      href: '',
      linkType: 'page',
      isIconOnly: false,
      iconElement: null,
      wireframeId: null,
      container: null,
      position: { x: 0, y: 0 }
    });
  };

  // ============== INPUT PLACEHOLDER EDITING FUNCTIONS ==============

  // Handle input/textarea click to open placeholder editor
  const handleInputPlaceholderClick = (e, element, wireframeId, container) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = element.getBoundingClientRect();
    const placeholder = element.getAttribute('placeholder') || '';

    setPlaceholderPopup({
      show: true,
      element: element,
      placeholder: placeholder,
      wireframeId: wireframeId,
      container: container,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.bottom + 10
      }
    });
  };

  // Save placeholder changes
  const handleSavePlaceholderPopup = () => {
    if (placeholderPopup.element) {
      placeholderPopup.element.setAttribute('placeholder', placeholderPopup.placeholder);
      markSectionModified(placeholderPopup.wireframeId, placeholderPopup.container);
    }

    setPlaceholderPopup({
      show: false,
      element: null,
      placeholder: '',
      wireframeId: null,
      container: null,
      position: { x: 0, y: 0 }
    });
  };

  // Close placeholder popup without saving
  const handleClosePlaceholderPopup = () => {
    setPlaceholderPopup({
      show: false,
      element: null,
      placeholder: '',
      wireframeId: null,
      container: null,
      position: { x: 0, y: 0 }
    });
  };

  // ============== IMAGE EDITING FUNCTIONS ==============

  // Check if element is an image or has background image
  const isImageElement = (element) => {
    if (!element) return false;
    return element.tagName === 'IMG';
  };

  // Check if element has background image
  const hasBackgroundImage = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const bgImage = style.backgroundImage;
    return bgImage && bgImage !== 'none' && bgImage.includes('url(');
  };

  // Handle Bootstrap icon click — opens icon picker modal
  const handleIconClick = (e, iconEl, wireframeId, container) => {
    e.preventDefault();
    e.stopPropagation();
    const iconClass = Array.from(iconEl.classList).find(c => c.startsWith('bi-') && c !== 'bi');
    const currentIcon = iconClass ? iconClass.replace('bi-', '') : '';
    setIconPickerModal({ show: true, currentIcon, iconElement: iconEl, wireframeId, container });
  };

  // Handle icon selected from picker — update DOM class + mark section modified
  const handleIconSelected = (newIconName) => {
    const { iconElement, wireframeId, container } = iconPickerModal;
    if (iconElement) {
      const oldClass = Array.from(iconElement.classList).find(c => c.startsWith('bi-') && c !== 'bi');
      if (oldClass) iconElement.classList.replace(oldClass, `bi-${newIconName}`);
      else iconElement.classList.add(`bi-${newIconName}`);
      markSectionModified(wireframeId, container);
    }
    setIconPickerModal({ show: false, currentIcon: '', iconElement: null, wireframeId: null, container: null });
  };

  // Handle image click
  const handleImageClick = (e, element, wireframeId, container, isBackground = false) => {
    e.preventDefault();
    e.stopPropagation();

    let currentSrc = '';
    if (isBackground) {
      const style = window.getComputedStyle(element);
      const bgImage = style.backgroundImage;
      // Extract URL from background-image
      const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
      currentSrc = match ? match[1] : '';
    } else {
      currentSrc = element.src || '';
    }

    setImageModal({
      show: true,
      element: element,
      currentSrc: currentSrc,
      isBackground: isBackground,
      wireframeId: wireframeId,
      container: container
    });
  };

  // Handle image selection from modal
  const handleImageSelect = (newImageUrl) => {
    if (imageModal.element) {
      if (imageModal.isBackground) {
        // Update background image - handle complex background shorthand with gradients
        const element = imageModal.element;
        const currentStyle = element.getAttribute('style') || '';

        // Check if using inline style with background shorthand (has gradient + url)
        if (currentStyle.includes('background:') && currentStyle.includes('url(')) {
          // Replace URL in the existing background shorthand
          const updatedStyle = currentStyle.replace(
            /url\(['"]?[^'")\s]+['"]?\)/g,
            `url('${newImageUrl}')`
          );
          element.setAttribute('style', updatedStyle);
        } else {
          // Simple background-image case
          element.style.backgroundImage = `url('${newImageUrl}')`;
        }
      } else {
        // Update img src
        imageModal.element.src = newImageUrl;
        // Ensure image maintains aspect ratio and fills container
        if (!imageModal.element.style.objectFit) {
          imageModal.element.style.objectFit = 'cover';
        }
      }

      // Mark section as modified
      markSectionModified(imageModal.wireframeId, imageModal.container);
    }

    // Close modal
    setImageModal({
      show: false,
      element: null,
      currentSrc: '',
      isBackground: false,
      wireframeId: null,
      container: null
    });
  };

  // Close image modal
  const handleCloseImageModal = () => {
    setImageModal({
      show: false,
      element: null,
      currentSrc: '',
      isBackground: false,
      wireframeId: null,
      container: null
    });
  };

  // ============== END IMAGE EDITING FUNCTIONS ==============

  // ============== MAP EDITING FUNCTIONS ==============

  // Handle map container click
  const handleMapClick = (e, mapContainer, wireframeId, container) => {
    e.preventDefault();
    e.stopPropagation();

    // Get current address from data attribute or nearby text
    const currentAddress = mapContainer.getAttribute('data-address') || '';

    // Find the address text element in the same section
    const addressElement = container.querySelector('.map-address-text');

    setMapModal({
      show: true,
      mapContainer: mapContainer,
      addressElement: addressElement,
      currentAddress: currentAddress,
      wireframeId: wireframeId,
      container: container
    });
  };

  // Handle map update from modal
  const handleMapUpdate = (mapData) => {
    if (mapModal.mapContainer) {
      // Update the iframe src
      const iframe = mapModal.mapContainer.querySelector('iframe');
      if (iframe) {
        iframe.src = mapData.embedUrl;
      }

      // Update the data attribute
      mapModal.mapContainer.setAttribute('data-address', mapData.address);

      // Update the address text if found
      if (mapModal.addressElement) {
        mapModal.addressElement.textContent = mapData.address;
      }

      // Mark section as modified
      markSectionModified(mapModal.wireframeId, mapModal.container);
    }

    // Close modal
    handleCloseMapModal();
  };

  // Close map modal
  const handleCloseMapModal = () => {
    setMapModal({
      show: false,
      mapContainer: null,
      addressElement: null,
      currentAddress: '',
      wireframeId: null,
      container: null
    });
  };

  // ============== END MAP EDITING FUNCTIONS ==============

  // Setup inline editing for a section
  const setupInlineEditing = (container, wireframeId) => {
    if (!container) return;

    // First, clean up any persisted editing attributes from saved HTML
    // This handles projects where html_content was saved with these attributes
    const editingAttrs = ['data-edit-setup', 'data-editable', 'data-bg-edit-setup', 'data-bg-editable', 'data-map-edit-setup'];
    container.querySelectorAll('*').forEach(el => {
      editingAttrs.forEach(attr => {
        if (el.hasAttribute(attr)) {
          el.removeAttribute(attr);
        }
      });
    });

    // Remove any persisted edit indicators
    container.querySelectorAll('.bg-image-edit-indicator, .map-edit-indicator').forEach(el => el.remove());

    // Expand all Bootstrap accordion panels so content is visible and editable
    container.querySelectorAll('.accordion-collapse').forEach(el => {
      el.classList.add('show');
      el.classList.remove('collapse', 'collapsing');
    });
    // Remove data-bs-toggle from accordion buttons to prevent collapse in editor
    container.querySelectorAll('[data-bs-toggle="collapse"]').forEach(el => {
      el.removeAttribute('data-bs-toggle');
      el.removeAttribute('data-bs-target');
      el.classList.remove('collapsed');
    });

    // Global click prevention for all links/buttons - use event delegation
    if (!container.hasAttribute('data-global-click-setup')) {
      container.setAttribute('data-global-click-setup', 'true');

      container.addEventListener('click', (e) => {
        // Find if the click target or any parent is a link or button
        let target = e.target;
        while (target && target !== container) {
          // Skip section-controls buttons - they have their own handlers
          if (target.classList.contains('section-control-btn') ||
              target.classList.contains('section-controls') ||
              target.closest('.section-controls')) {
            return; // Let the event bubble up to the section-control-btn handlers
          }
          if (target.tagName === 'A' || target.tagName === 'BUTTON') {
            e.preventDefault();
            e.stopPropagation();
            // Always handle link/button clicks to show edit popup
            handleLinkButtonClick(e, target, wireframeId, container);
            return;
          }
          target = target.parentElement;
        }
      }, true); // Use capture phase to catch events early
    }

    // Find all potential editable elements (including buttons and links)
    // Exclude section-controls buttons which have their own handlers
    const elements = container.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, small, li, td, th, label, div, a, button:not(.section-control-btn), .accordion-body, [data-editable-text]');

    elements.forEach(el => {
      // Skip if already setup
      if (el.hasAttribute('data-edit-setup')) return;
      // Skip section-controls elements
      if (el.closest('.section-controls')) return;
      el.setAttribute('data-edit-setup', 'true');

      // Handle links and buttons specially
      if (isLinkOrButton(el)) {
        el.style.cursor = 'pointer';
        el.setAttribute('data-editable', 'true');

        // Prevent default navigation/action
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleLinkButtonClick(e, el, wireframeId, container);
        });

        return; // Don't make contentEditable
      }

      // Regular text elements
      if (isEditableElement(el)) {
        el.style.cursor = 'text';
        el.setAttribute('data-editable', 'true');

        // Add focus/blur handlers
        el.addEventListener('focus', () => {
          el.style.outline = '2px solid var(--accent, #7c3aed)';
          el.style.outlineOffset = '2px';
          // Store original content to detect actual changes
          el._originalContent = el.innerHTML;
        });

        el.addEventListener('blur', () => {
          el.style.outline = 'none';
          // Only mark as modified if content actually changed
          if (el._originalContent !== undefined && el.innerHTML !== el._originalContent) {
            markSectionModified(wireframeId, container);
          }
          delete el._originalContent;
        });

        // Make content editable
        el.contentEditable = 'true';
      }
    });

    // Setup image editing
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      if (img.hasAttribute('data-edit-setup')) return;
      img.setAttribute('data-edit-setup', 'true');
      img.setAttribute('data-editable', 'true');
      img.style.cursor = 'pointer';

      img.addEventListener('click', (e) => {
        handleImageClick(e, img, wireframeId, container, false);
      });
    });

    // Setup Bootstrap icon editing
    const iconElements = container.querySelectorAll('i[class*="bi-"]');
    iconElements.forEach(icon => {
      if (icon.hasAttribute('data-edit-setup')) return;
      if (icon.closest('.section-controls')) return;
      icon.setAttribute('data-edit-setup', 'true');
      icon.style.cursor = 'pointer';

      icon.addEventListener('mouseenter', () => {
        icon.style.outline = '2px dashed #7c3aed';
        icon.style.outlineOffset = '3px';
        icon.style.borderRadius = '3px';
      });
      icon.addEventListener('mouseleave', () => {
        icon.style.outline = 'none';
      });
      icon.addEventListener('click', (e) => {
        handleIconClick(e, icon, wireframeId, container);
      });
    });

    // Setup input/textarea placeholder editing
    const inputElements = container.querySelectorAll('input, textarea');
    inputElements.forEach(inputEl => {
      if (inputEl.hasAttribute('data-edit-setup')) return;
      if (inputEl.closest('.section-controls')) return;
      inputEl.setAttribute('data-edit-setup', 'true');
      inputEl.setAttribute('data-editable', 'true');
      inputEl.style.cursor = 'pointer';

      // Prevent typing in the input - it should only open the popup
      inputEl.setAttribute('readonly', 'true');

      inputEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleInputPlaceholderClick(e, inputEl, wireframeId, container);
      });

      // Add hover effect
      inputEl.addEventListener('mouseenter', () => {
        inputEl.style.outline = '2px dashed #7c3aed';
        inputEl.style.outlineOffset = '2px';
      });
      inputEl.addEventListener('mouseleave', () => {
        inputEl.style.outline = 'none';
      });
    });

    // Setup background image editing (sections, divs with background-image)
    // Look for elements with inline style containing url() - covers both background: and background-image:
    const allElements = container.querySelectorAll('section, div, [style*="url("], [style*="background"]');
    allElements.forEach(el => {
      if (el.hasAttribute('data-bg-edit-setup')) return;

      // Check if element actually has a background image
      const inlineStyle = el.getAttribute('style') || '';
      const hasUrlInStyle = inlineStyle.includes('url(');

      // Also check computed style for background-image
      if (!hasUrlInStyle && !hasBackgroundImage(el)) return;

      el.setAttribute('data-bg-edit-setup', 'true');
      el.setAttribute('data-bg-editable', 'true');
      el.style.cursor = 'pointer';

      // Add edit button indicator with icon and text
      const editIndicator = document.createElement('div');
      editIndicator.className = 'bg-image-edit-indicator';
      editIndicator.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg><span>Change Image</span>';
      editIndicator.title = 'Click to change background image';

      // Make element relative if not already positioned
      const computedStyle = window.getComputedStyle(el);
      if (computedStyle.position === 'static') {
        el.style.position = 'relative';
      }

      el.appendChild(editIndicator);

      el.addEventListener('mouseenter', () => {
        editIndicator.style.opacity = '1';
      });

      el.addEventListener('mouseleave', () => {
        editIndicator.style.opacity = '0';
      });

      // Click on the edit indicator
      editIndicator.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleImageClick(e, el, wireframeId, container, true);
      });

      // Also allow double-click on the element itself to edit background
      el.addEventListener('dblclick', (e) => {
        // Only handle if clicking on the element itself, not its children
        if (e.target === el || e.target === editIndicator) {
          e.preventDefault();
          e.stopPropagation();
          handleImageClick(e, el, wireframeId, container, true);
        }
      });
    });

    // Setup map editing
    const mapContainers = container.querySelectorAll('[data-map-editable="true"], .map-container');
    mapContainers.forEach(mapContainer => {
      if (mapContainer.hasAttribute('data-map-edit-setup')) return;

      mapContainer.setAttribute('data-map-edit-setup', 'true');
      mapContainer.style.cursor = 'pointer';

      // Add map edit indicator - centered overlay with icon and text
      const mapIndicator = document.createElement('div');
      mapIndicator.className = 'map-edit-indicator';
      mapIndicator.innerHTML = `
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
        <span>Click to Edit Location</span>
      `;
      mapIndicator.title = 'Click to update map location';

      // Make container relative if needed
      const computedStyle = window.getComputedStyle(mapContainer);
      if (computedStyle.position === 'static') {
        mapContainer.style.position = 'relative';
      }

      mapContainer.appendChild(mapIndicator);

      mapContainer.addEventListener('mouseenter', () => {
        mapIndicator.style.opacity = '1';
      });

      mapContainer.addEventListener('mouseleave', () => {
        mapIndicator.style.opacity = '0';
      });

      // Click on indicator to edit map
      mapIndicator.addEventListener('click', (e) => {
        handleMapClick(e, mapContainer, wireframeId, container);
      });

      // Double-click on map container to edit
      mapContainer.addEventListener('dblclick', (e) => {
        handleMapClick(e, mapContainer, wireframeId, container);
      });
    });
  };

  // Mark a section as modified and store its current HTML
  const markSectionModified = (wireframeId, container) => {
    if (!container) return;

    // Get the dynamic-component div which contains the actual HTML
    const dynamicComponent = container.querySelector('.dynamic-component');
    if (dynamicComponent) {
      // Clone the content and remove <style> elements (CSS variables are stored in project's style_guide_json)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = dynamicComponent.innerHTML;

      // Remove only the :root CSS variables <style> block (injected by renderComponentDynamic)
      // Keep component-scoped <style> blocks (e.g., .stylist-map-*, .kale-map-*) so themed layouts are preserved
      tempDiv.querySelectorAll('style').forEach(el => {
        if (el.textContent.includes(':root')) {
          el.remove();
        }
      });

      // Remove editing-related attributes that shouldn't be persisted
      // These are added by setupInlineEditing and would prevent re-attaching handlers on reload
      const editingAttrs = ['data-edit-setup', 'data-editable', 'data-bg-edit-setup', 'data-bg-editable', 'data-map-edit-setup', 'data-global-click-setup', 'contenteditable'];
      tempDiv.querySelectorAll('*').forEach(el => {
        editingAttrs.forEach(attr => el.removeAttribute(attr));
        // Remove inline cursor style added for editing
        if (el.style.cursor === 'pointer' || el.style.cursor === 'text') {
          el.style.removeProperty('cursor');
        }
      });

      // Remove readonly attribute added to inputs/textareas for editing mode
      tempDiv.querySelectorAll('input[readonly], textarea[readonly]').forEach(el => {
        el.removeAttribute('readonly');
      });

      // Remove edit indicators (background image edit buttons, map edit overlays)
      tempDiv.querySelectorAll('.bg-image-edit-indicator, .map-edit-indicator').forEach(el => el.remove());

      // Restore Bootstrap accordion attributes removed during editing
      tempDiv.querySelectorAll('.accordion-collapse').forEach(el => {
        el.classList.add('collapse');
        el.classList.remove('show');
      });
      tempDiv.querySelectorAll('.accordion-button').forEach(el => {
        const targetId = el.closest('.accordion-item')?.querySelector('.accordion-collapse')?.id;
        if (targetId) {
          el.setAttribute('data-bs-toggle', 'collapse');
          el.setAttribute('data-bs-target', '#' + targetId);
          el.classList.add('collapsed');
        }
      });

      const currentHtml = tempDiv.innerHTML;
      // Store in ref silently (no DOM replacement) to avoid destroying editor state
      modifiedSectionsRef.current = { ...modifiedSectionsRef.current, [wireframeId]: currentHtml };
      // Bump counter so save button shows updated count without re-rendering section HTML
      setEditCount(c => c + 1);
    }
  };

  // Sync ref to state — call before save or when UI needs to reflect changes
  const syncModifiedSections = () => {
    const refData = modifiedSectionsRef.current;
    if (Object.keys(refData).length > 0) {
      setModifiedSections(prev => ({ ...prev, ...refData }));
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return Object.keys(modifiedSections).length > 0 || Object.keys(modifiedSectionsRef.current).length > 0;
  };

  // Navigate to dashboard with unsaved changes confirmation
  const handleBackToDashboard = useCallback(() => {
    if (Object.keys(modifiedSections).length > 0 || Object.keys(modifiedSectionsRef.current).length > 0) {
      setShowLeaveConfirm(true);
    } else {
      history.push('/');
    }
  }, [modifiedSections, history]);

  // Save all modified sections
  const handleSaveAllModified = async () => {
    // Merge ref data (from inline edits) with state data (from AI regeneration)
    const allModified = { ...modifiedSections, ...modifiedSectionsRef.current };
    const modifiedIds = Object.keys(allModified);
    if (modifiedIds.length === 0) {
      cuteToast({
        type: "info",
        title: "No Changes",
        message: "No modifications to save",
        timer: 2000,
      });
      return;
    }

    setIsSaving(true);
    let savedCount = 0;
    let errorCount = 0;

    for (const wireframeId of modifiedIds) {
      const htmlContent = allModified[wireframeId];

      try {
        await new Promise((resolve, reject) => {
          sendRequest(
            {
              url: `${endPoints.projects}/${project_id}/wireframes/update`,
              method: "POST",
              body: {
                wireframe_id: parseInt(wireframeId),
                html_content: htmlContent,
              },
            },
            (response) => {
              if (response.status === 200) {
                savedCount++;
                resolve();
              } else {
                errorCount++;
                reject(new Error(response.message));
              }
            }
          );
        });
      } catch (error) {
        console.error('Error saving section:', wireframeId, error);
      }
    }

    setIsSaving(false);
    setModifiedSections({});
    modifiedSectionsRef.current = {};

    if (savedCount > 0) {
      cuteToast({
        type: "success",
        title: "Saved",
        message: `${savedCount} section${savedCount > 1 ? 's' : ''} saved successfully`,
        timer: 2000,
      });
      loadProject(); // Reload to get updated data
    }
    if (errorCount > 0) {
      cuteToast({
        type: "error",
        title: "Error",
        message: `Failed to save ${errorCount} section${errorCount > 1 ? 's' : ''}`,
        timer: 3000,
      });
    }
  };

  // ============== END INLINE EDITING FUNCTIONS ==============

  const handleZoomIn = () => setZoom((prev) => Math.min(150, prev + 10));
  const handleZoomOut = () => setZoom((prev) => Math.max(25, prev - 10));
  const handleFitScreen = () => setZoom(70); // Adjusted for wider 1440px viewport

  const getDeviceWidth = () => {
    switch (deviceMode) {
      case "mobile":
        return "375px";
      case "tablet":
        return "768px";
      default:
        return "1440px"; // Wider viewport for proper container margins
    }
  };

  const getSectionTitle = (componentCode) => {
    const parts = componentCode.split("_");
    // For themed components like CLIARO_HERO_01, use the second part (HERO)
    // For classic components like HERO_01, use the first part (HERO)
    const namePart = parts.length >= 3 ? parts[1] : parts[0];
    return namePart.charAt(0) + namePart.slice(1).toLowerCase();
  };

  const groupWireframesByPage = () => {
    if (!wireframes || wireframes.length === 0) return [];

    // Separate global wireframes (header/footer) from page-specific wireframes
    const globalHeader = wireframes.find(w => w.is_global && /HEADER_/i.test(w.component_code));
    const globalFooter = wireframes.find(w => w.is_global && /FOOTER_/i.test(w.component_code));
    const pageWireframes = wireframes.filter(w => !w.is_global && w.page_id !== '_global');

    const pageMap = {};
    pageWireframes.forEach((wireframe) => {
      const pageId = wireframe.page_id || "page-1";
      if (!pageMap[pageId]) {
        pageMap[pageId] = {
          page_id: pageId,
          name: wireframe.page_name || "Untitled Page",
          route: wireframe.page_route || "/",
          page_type: wireframe.page_type || 'regular',
          sections: [],
        };
      }
      pageMap[pageId].sections.push(wireframe);
    });

    // Convert to array and sort - Home page first, then by page_id
    const pages = Object.values(pageMap).sort((a, b) => {
      // Check if either is home page (by name or route)
      const aIsHome = a.name.toLowerCase() === 'home' || a.route === '/';
      const bIsHome = b.name.toLowerCase() === 'home' || b.route === '/';

      if (aIsHome && !bIsHome) return -1;
      if (!aIsHome && bIsHome) return 1;

      // Otherwise sort by page_id
      return a.page_id.localeCompare(b.page_id);
    });

    // Add global header at the beginning and footer at the end of each page's sections
    pages.forEach(page => {
      // Sort page sections by section_order
      page.sections.sort((a, b) => (a.section_order || 0) - (b.section_order || 0));

      // Prepend global header
      if (globalHeader) {
        page.sections.unshift({ ...globalHeader, _isGlobalHeader: true });
      }

      // Append global footer
      if (globalFooter) {
        page.sections.push({ ...globalFooter, _isGlobalFooter: true });
      }
    });

    return pages;
  };

  // Special renderer for header component with dynamic navigation
  const renderHeaderComponent = (content, styleGuide) => {
    const cssVars = generateCSSVariables(styleGuide || currentStyle);

    // Merge with sample data to get defaults for any missing fields
    const sampleData = getSampleData('HEADER_01') || {};
    const mergedContent = { ...sampleData, ...content };

    // Use default logo if content has placeholder or empty logo
    const isPlaceholderLogo = mergedContent.logo_url?.includes('placehold.co');
    const logoUrl = (!mergedContent.logo_url || isPlaceholderLogo)
      ? sampleData.logo_url
      : mergedContent.logo_url;

    // Build navigation links dynamically - only include links with text
    const navLinks = [];
    for (let i = 1; i <= 5; i++) {
      const text = mergedContent[`nav_link_${i}_text`];
      const url = mergedContent[`nav_link_${i}_url`] || '#';
      if (text && text.trim()) {
        navLinks.push({ text, url });
      }
    }

    const headerHtml = `
      <style>${cssVars}</style>
      <header class="py-3" style="background: var(--color-background); border-bottom: 1px solid var(--color-muted);">
        <div class="container">
          <nav class="navbar navbar-expand-lg">
            <a class="navbar-brand d-flex align-items-center" href="${mergedContent.logo_link || '/'}">
              <img src="${logoUrl}" alt="${mergedContent.logo_alt || 'Logo'}" style="max-width: 180px; max-height: 50px; width: auto; height: auto; object-fit: contain;">
            </a>
            <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" style="border-color: var(--color-muted);">
              <span class="navbar-toggler-icon"></span>
            </button>
            <div class="collapse navbar-collapse" id="navbarNav">
              <ul class="navbar-nav ms-auto mb-2 mb-lg-0" style="font-family: var(--font-body); font-weight: var(--font-body-weight);">
                ${navLinks.map(link => `
                  <li class="nav-item">
                    <a class="nav-link" href="${link.url}" style="color: var(--color-text);">${link.text}</a>
                  </li>
                `).join('')}
              </ul>
              ${mergedContent.cta_text ? `
                <a href="${mergedContent.cta_link || '#'}" class="btn ms-lg-3" style="background: var(--btn-bg); color: var(--btn-text); border-radius: var(--btn-radius); padding: var(--btn-padding);">
                  ${mergedContent.cta_text}
                </a>
              ` : ''}
            </div>
          </nav>
        </div>
      </header>
    `;

    return headerHtml;
  };

  const renderWireframeContent = (wireframe) => {
    const componentType = wireframe.component_code.split("_")[0];

    // PRIORITY 0: Check modifiedSections first (for AI regenerated content preview)
    if (modifiedSections[wireframe.id]) {
      return (
        <div
          className="dynamic-component"
          data-wireframe-id={wireframe.id}
          dangerouslySetInnerHTML={{ __html: modifiedSections[wireframe.id] }}
        />
      );
    }

    // PRIORITY 1: Use stored html_content if available (for inline edited content)
    if (wireframe.html_content) {
      return (
        <div
          className="dynamic-component"
          data-wireframe-id={wireframe.id}
          dangerouslySetInnerHTML={{ __html: wireframe.html_content }}
        />
      );
    }

    // PRIORITY 2: Parse content_json and render with template
    let content;
    if (wireframe.content_json) {
      try {
        content = JSON.parse(wireframe.content_json);
      } catch (e) {
        content = null;
      }
    }

    // Generate default content if none exists - use sample_data_json from component templates
    if (!content) {
      // Try to get sample data from JSON template
      content = getSampleData(wireframe.component_code);

      // Fallback for unknown components
      if (!content) {
        content = {
          heading: wireframe.component_code.replace(/_/g, " "),
          description: wireframe.purpose || "Section content"
        };
      }
    }

    try {
      // Special handling for classic HEADER_01 - dynamic nav rendering
      if (wireframe.component_code === 'HEADER_01') {
        const renderedHtml = renderHeaderComponent(content, currentStyle);
        return (
          <div
            className="dynamic-component"
            data-wireframe-id={wireframe.id}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        );
      }

      // Check if we have an HTML template for this component - use dynamic rendering
      const htmlTemplate = getHtmlTemplate(wireframe.component_code);

      if (htmlTemplate) {
        // Use the dynamic renderer - renders HTML template with content and style guide
        const renderedHtml = renderComponentDynamic(
          wireframe.component_code,
          content,
          currentStyle
        );

        return (
          <div
            className="dynamic-component"
            data-wireframe-id={wireframe.id}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        );
      }

      // Fallback for components without HTML templates - simple generic render
      const colors = currentStyle.colors;
      const fonts = currentStyle.fonts;

      return (
        <section style={{
          backgroundColor: colors.background,
          padding: "60px 20px",
          fontFamily: fonts.body
        }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <h2 style={{
              fontSize: "36px",
              fontWeight: "700",
              color: colors.text,
              marginBottom: "16px",
              fontFamily: fonts.heading
            }}>
              {content.heading || content.headline || content.section_title || wireframe.component_code}
            </h2>
            <p style={{ fontSize: "16px", color: colors.muted, marginBottom: "32px" }}>
              {content.description || content.subheading || content.tagline || ""}
            </p>
            {/* Render any items/list content */}
            {content.items && Array.isArray(content.items) && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px" }}>
                {content.items.map((item, i) => (
                  <div key={i} style={{ padding: "20px", border: `1px solid ${colors.muted}30`, borderRadius: "8px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: "600", color: colors.text, marginBottom: "8px" }}>
                      {item.title || item.name || `Item ${i + 1}`}
                    </h3>
                    <p style={{ fontSize: "14px", color: colors.muted }}>
                      {item.description || item.text || ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {/* Show CTA button if present */}
            {content.cta_text && (
              <button style={{
                backgroundColor: colors.primary,
                color: "#ffffff",
                padding: currentStyle.buttons?.padding || "12px 24px",
                borderRadius: currentStyle.buttons?.borderRadius || "8px",
                border: "none",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                marginTop: "24px"
              }}>
                {content.cta_text}
              </button>
            )}
          </div>
        </section>
      );

    } catch (e) {
      return (
        <div className="section-error" style={{ padding: "60px 20px", textAlign: "center", backgroundColor: "#fee" }}>
          <h3 style={{ color: "#c00" }}>Error Rendering Content</h3>
          <p style={{ color: "#666", fontSize: "14px" }}>{e.message}</p>
        </div>
      );
    }
  };

  // --- Autoresponder Panel Logic ---
  const providerLabels = {
    convertkit: 'ConvertKit',
    mailchimp: 'Mailchimp',
    getresponse: 'GetResponse',
    'active campaign': 'ActiveCampaign'
  };

  const listFieldLabel = (provider) => {
    if (provider === 'convertkit') return 'Tag';
    if (provider === 'getresponse') return 'Campaign';
    return 'List';
  };

  const loadAutoresponderPanel = () => {
    if (arLoaded) return;
    // Load connected providers
    sendRequest({ url: endPoints.fetchAllKeys, method: 'GET' }, (response) => {
      const connected = (response.body || []).filter(p => p.status === 1);
      setArProviders(connected);
    });
    // Load saved project config
    sendRequest({ url: `${endPoints.projects}/${project_id}/autoresponder`, method: 'GET' }, (response) => {
      if (response.status === 200 && response.body) {
        setArConfig(response.body);
      }
    });
    setArLoaded(true);
  };

  const handleArProviderChange = (provider) => {
    setArConfig({ provider, list_id: '', list_name: '' });
    setArLists([]);
    if (!provider) return;

    const providerData = arProviders.find(p => p.meta_key === provider);
    if (!providerData) return;

    const creds = JSON.parse(providerData.meta_data);
    setArLoadingLists(true);

    let url, body;
    if (provider === 'convertkit') {
      url = endPoints.convertkitTags;
      body = { ck_key: creds.ck_key };
    } else if (provider === 'mailchimp') {
      url = endPoints.mailchimpLists;
      body = { mc_key: creds.mc_key };
    } else if (provider === 'getresponse') {
      url = endPoints.getResponseLists;
      body = { gr_key: creds.gr_key };
    } else if (provider === 'active campaign') {
      url = endPoints.activeCampaignLists;
      body = { ac_key: creds.ac_key, ac_url: creds.ac_url };
    }

    if (url) {
      sendRequest({ url, method: 'POST', body }, (response) => {
        setArLoadingLists(false);
        setArLists(response.data || []);
      });
    }
  };

  const handleArListChange = (list_id) => {
    const list = arLists.find(l => String(l.id) === String(list_id));
    setArConfig(prev => ({ ...prev, list_id, list_name: list ? list.name : '' }));
  };

  const saveArConfig = () => {
    setArSaving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/autoresponder`,
      method: 'PUT',
      body: arConfig
    }, (response) => {
      setArSaving(false);
      if (response.status === 200) {
        cuteToast({ type: 'success', title: 'Autoresponder config saved' });
      }
    });
  };

  const removeArConfig = () => {
    setArSaving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/autoresponder`,
      method: 'PUT',
      body: { provider: '', list_id: '', list_name: '' }
    }, (response) => {
      setArSaving(false);
      if (response.status === 200) {
        setArConfig({ provider: '', list_id: '', list_name: '' });
        setArLists([]);
        cuteToast({ type: 'success', title: 'Autoresponder disconnected' });
      }
    });
  };

  const handleArDragStart = (e) => {
    setArDragging(true);
    setArDragOffset({ x: e.clientX - arPosition.x, y: e.clientY - arPosition.y });
  };

  useEffect(() => {
    if (!arDragging) return;
    const handleMove = (e) => setArPosition({ x: e.clientX - arDragOffset.x, y: e.clientY - arDragOffset.y });
    const handleUp = () => setArDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [arDragging, arDragOffset]);

  // SEO panel handlers
  const handleSeoDragStart = (e) => {
    setSeoDragging(true);
    setSeoDragOffset({ x: e.clientX - seoPosition.x, y: e.clientY - seoPosition.y });
  };

  useEffect(() => {
    if (!seoDragging) return;
    const handleMove = (e) => setSeoPosition({ x: e.clientX - seoDragOffset.x, y: e.clientY - seoDragOffset.y });
    const handleUp = () => setSeoDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [seoDragging, seoDragOffset]);

  const loadSeoSettings = () => {
    sendRequest({
      url: `${endPoints.projects}/${project_id}/site-settings`,
      method: "GET",
    }, (response) => {
      if (response.status === 200 && response.body) {
        setSeoSettings(response.body);
        setSeoLoaded(true);
      }
    });
  };

  const saveSeoSettings = () => {
    setSeoSaving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/site-settings`,
      method: "PUT",
      body: seoSettings,
    }, (response) => {
      setSeoSaving(false);
      if (response.status === 200) {
        cuteToast({ type: "success", title: "SEO settings saved", message: "" });
      } else {
        cuteToast({ type: "error", title: "Failed to save", message: response.message || "" });
      }
    });
  };

  // Custom Code panel handlers
  const handleCustomCodeDragStart = (e) => {
    setCustomCodeDragging(true);
    setCustomCodeDragOffset({ x: e.clientX - customCodePosition.x, y: e.clientY - customCodePosition.y });
  };

  useEffect(() => {
    if (!customCodeDragging) return;
    const handleMove = (e) => setCustomCodePosition({ x: e.clientX - customCodeDragOffset.x, y: e.clientY - customCodeDragOffset.y });
    const handleUp = () => setCustomCodeDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [customCodeDragging, customCodeDragOffset]);

  const loadCustomCode = () => {
    sendRequest({
      url: `${endPoints.projects}/${project_id}/custom-code`,
      method: "GET",
    }, (response) => {
      if (response.status === 200 && response.body) {
        setCustomCodeData({
          custom_css: response.body.custom_css ? decodeURIComponent(escape(atob(response.body.custom_css))) : '',
          custom_script: response.body.custom_script ? decodeURIComponent(escape(atob(response.body.custom_script))) : ''
        });
        setCustomCodeLoaded(true);
      }
    });
  };

  const saveCustomCode = () => {
    setCustomCodeSaving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/custom-code`,
      method: "PUT",
      body: {
        custom_css: btoa(unescape(encodeURIComponent(customCodeData.custom_css || ''))),
        custom_script: btoa(unescape(encodeURIComponent(customCodeData.custom_script || '')))
      },
    }, (response) => {
      setCustomCodeSaving(false);
      if (response.status === 200) {
        cuteToast({ type: "success", title: "Custom code saved", message: "" });
      } else {
        cuteToast({ type: "error", title: "Failed to save", message: response.message || "" });
      }
    });
  };

  // CNAME panel handlers
  const handleCnameDragStart = (e) => {
    setCnameDragging(true);
    setCnameDragOffset({ x: e.clientX - cnamePosition.x, y: e.clientY - cnamePosition.y });
  };

  useEffect(() => {
    if (!cnameDragging) return;
    const handleMove = (e) => setCnamePosition({ x: e.clientX - cnameDragOffset.x, y: e.clientY - cnameDragOffset.y });
    const handleUp = () => setCnameDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [cnameDragging, cnameDragOffset]);

  const loadCnameConfig = () => {
    sendRequest({
      url: `${endPoints.projects}/${project_id}/cname`,
      method: "GET",
    }, (response) => {
      if (response.status === 200 && response.body) {
        setCnameData(response.body);
        setCnameInput(response.body.custom_domain || '');
        setCnameLoaded(true);
      }
    });
  };

  const saveCnameConfig = () => {
    if (!cnameInput.trim()) {
      cuteToast({ type: "error", title: "Domain required", message: "" });
      return;
    }
    setCnameSaving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/cname`,
      method: "PUT",
      body: { custom_domain: cnameInput.trim() },
    }, (response) => {
      setCnameSaving(false);
      if (response.status === 200) {
        setCnameData({ ...cnameData, custom_domain: cnameInput.trim() });
        cuteToast({ type: "success", title: "Custom domain saved", message: "" });
      } else {
        cuteToast({ type: "error", title: "Failed", message: response.message || "" });
      }
    });
  };

  const removeCnameConfig = () => {
    setCnameRemoving(true);
    sendRequest({
      url: `${endPoints.projects}/${project_id}/cname`,
      method: "DELETE",
    }, (response) => {
      setCnameRemoving(false);
      if (response.status === 200) {
        setCnameData({ ...cnameData, custom_domain: '' });
        setCnameInput('');
        cuteToast({ type: "success", title: "Custom domain removed", message: "" });
      } else {
        cuteToast({ type: "error", title: "Failed", message: response.message || "" });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="editor-loading">
        <div className="spinner"></div>
        <p>Loading project...</p>
      </div>
    );
  }

  const pages = groupWireframesByPage();

  return (
    <div className="wireframe-editor">
      {/* Top Toolbar */}
      <header className="editor-toolbar">
        <div className="toolbar-left">
          <button onClick={handleBackToDashboard} style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <img
              src={endPoints.logo}
              alt="Logo"
              style={{ height: "32px" }}
            />
          </button>
          <div className="toolbar-divider"></div>
          <button
            onClick={handleBackToDashboard}
            className="toolbar-btn"
            style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", color: "inherit", font: "inherit" }}
            title="Back to Dashboard"
          >
            <FiArrowLeft size={14} /> Dashboard
          </button>
          <div className="toolbar-divider"></div>
          <span className="project-name">{currentProject?.name || "Untitled"}</span>
          {(() => {
            // Parse AI usage from JSON
            let aiUsage = null;
            if (currentProject?.ai_usage_json) {
              try {
                aiUsage = JSON.parse(currentProject.ai_usage_json);
              } catch (e) {
                aiUsage = null;
              }
            }
            return null;
          })()}
        </div>

        <div className="toolbar-center">
          {/* Device preview buttons removed — media queries don't respond to element width,
              needs iframe-based preview or container queries to work properly */}
        </div>

        <div className="toolbar-right">
          <button className="toolbar-btn panel-btn-style" onClick={() => setShowStyleGuide(!showStyleGuide)}>
            <FiSettings /> Style&nbsp;Guide
          </button>
          <button className="toolbar-btn panel-btn-seo" onClick={() => { setShowSeoPanel(!showSeoPanel); if (!seoLoaded) loadSeoSettings(); }}>
            <FiGlobe /> SEO
          </button>
          <button className="toolbar-btn panel-btn-ar" onClick={() => { setShowAutoresponder(!showAutoresponder); if (!arLoaded) loadAutoresponderPanel(); }}>
            <FiMail /> Autoresponder
          </button>
          <button className="toolbar-btn panel-btn-code" onClick={() => { setShowCustomCodePanel(!showCustomCodePanel); if (!customCodeLoaded) loadCustomCode(); }}>
            <FiCode /> Custom&nbsp;Code
          </button>
          <button className="toolbar-btn panel-btn-cname" onClick={() => { setShowCnamePanel(!showCnamePanel); if (!cnameLoaded) loadCnameConfig(); }}>
            <FiLink /> Domain
          </button>
          {currentProject?.status === 'published' && currentProject?.subdomain && (
            <button
              className="toolbar-btn preview-btn"
              onClick={() => window.open(getSubdomainUrl(currentProject.subdomain), '_blank')}
            >
              <FiEye /> Preview
            </button>
          )}
          <button
            className={`btn-save ${hasUnsavedChanges() ? 'has-changes' : ''}`}
            onClick={() => {
              handleSaveStyleGuide();
              handleSaveAllModified();
            }}
            disabled={isSaving}
          >
            <FiSave className="me-1" />
            {isSaving ? 'Saving...' : hasUnsavedChanges() ? `Save (${Object.keys({ ...modifiedSections, ...modifiedSectionsRef.current }).length})` : 'Save'}
          </button>
          <button
            className={`btn-publish ${currentProject?.status === 'published' ? 'published' : ''}`}
            onClick={() => setShowPublishModal(true)}
          >
            {currentProject?.status === 'published' ? (
              <>
                <FiCheckCircle className="me-1" />
                Published
              </>
            ) : (
              <>
                <FiGlobe className="me-1" />
                Publish
              </>
            )}
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="editor-layout">
        {/* Left Sidebar - Pages & Sections */}
        <aside className="left-panel">
          <div className="panel-header">
            <span className="panel-title">PAGES</span>
          </div>
          <div className="panel-content">
            <div className="page-list">
              {pages.filter(p => p.page_type !== 'legal').map((page) => {
                const pageIndex = pages.indexOf(page);
                return (
                <div key={page.page_id}>
                  <div
                    className={`page-item ${expandedPages[pageIndex] ? "expanded" : ""} ${currentPageId === page.page_id ? "active" : ""}`}
                    onClick={() => {
                      togglePage(pageIndex);
                      handlePageSelect(page.page_id);
                    }}
                  >
                    {expandedPages[pageIndex] ? <FiChevronDown /> : <FiChevronRight />}
                    <span className="page-name">{page.name}</span>
                    <span className="section-count">{page.sections.length}</span>
                  </div>
                  {expandedPages[pageIndex] && (
                    <div className="section-list">
                      {page.sections.map((section, sectionIndex) => (
                        <div
                          key={section.id || sectionIndex}
                          className={`section-item ${
                            selectedSection?.id === section.id ? "active" : ""
                          } ${section.is_global || section._isGlobalHeader || section._isGlobalFooter ? "global-section" : ""}`}
                          onClick={() => { setSelectedSection(section); scrollToSection(section.id); }}
                        >
                          <span className="section-icon">
                            {getSectionTitle(section.component_code).charAt(0)}
                          </span>
                          <span>{getSectionTitle(section.component_code)}</span>
                          {(section.is_global || section._isGlobalHeader || section._isGlobalFooter) && (
                            <span className="global-badge">Global</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
              {pages.filter(p => p.page_type === 'legal').length > 0 && (
                <>
                  <div className="legal-pages-divider">
                    <span>LEGAL</span>
                  </div>
                  {pages.filter(p => p.page_type === 'legal').map((page) => {
                    const pageIndex = pages.indexOf(page);
                    return (
                      <div key={page.page_id}>
                        <div
                          className={`page-item ${expandedPages[pageIndex] ? "expanded" : ""} ${currentPageId === page.page_id ? "active" : ""}`}
                          onClick={() => {
                            togglePage(pageIndex);
                            handlePageSelect(page.page_id);
                          }}
                        >
                          {expandedPages[pageIndex] ? <FiChevronDown /> : <FiChevronRight />}
                          <span className="page-name">{page.name}</span>
                          <span className="section-count">{page.sections.length}</span>
                        </div>
                        {expandedPages[pageIndex] && (
                          <div className="section-list">
                            {page.sections.map((section, sectionIndex) => (
                              <div
                                key={section.id || sectionIndex}
                                className={`section-item ${selectedSection?.id === section.id ? "active" : ""}`}
                                onClick={() => { setSelectedSection(section); scrollToSection(section.id); }}
                              >
                                <span className="section-icon">L</span>
                                <span>{section.page_name || page.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Canvas Area */}
        <main className="canvas-area">
          <div className="canvas-container" ref={canvasRef}>
            <div
              className="preview-frame"
              style={{
                transform: `scale(${zoom / 100})`,
                width: getDeviceWidth(),
              }}
              onClick={(e) => {
                // Prevent any link navigation in the preview frame
                let target = e.target;
                while (target && target.className !== 'preview-frame') {
                  if (target.tagName === 'A') {
                    e.preventDefault();
                    return;
                  }
                  target = target.parentElement;
                }
              }}
            >
              {/* Inject CSS variables at preview frame level so all sections can use them */}
              <style dangerouslySetInnerHTML={{ __html: generateCSSVariables(currentStyle) }} />

              {(() => {
                // Get current page with its sections (including global header/footer)
                const currentPage = pages.find(p => p.page_id === currentPageId);
                const currentSections = currentPage?.sections || [];

                if (!currentPageId) {
                  return (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "400px",
                      color: "var(--text-muted)",
                      fontSize: "14px"
                    }}>
                      Select a page from the left sidebar to view its sections
                    </div>
                  );
                }

                if (currentSections.length === 0) {
                  return (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "400px",
                      color: "var(--text-muted)",
                      fontSize: "14px"
                    }}>
                      This page has no sections yet
                    </div>
                  );
                }

                return currentSections.map((section, index) => (
                <div
                  key={section.id || index}
                  data-section-id={section.id}
                  className={`site-section ${
                    hoveredSection?.id === section.id ? "hovered" : ""
                  } ${selectedSection?.id === section.id ? "selected" : ""} ${
                    modifiedSections[section.id] ? "modified" : ""
                  }`}
                  onClick={() => setSelectedSection(section)}
                  onMouseEnter={() => setHoveredSection(section)}
                  onMouseLeave={() => setHoveredSection(null)}
                  style={{
                    opacity: section.is_visible === false ? 0.4 : 1,
                    position: 'relative'
                  }}
                >
                  <span className="section-label">
                    {getSectionTitle(section.component_code)}
                    {(section.is_global || section._isGlobalHeader || section._isGlobalFooter) && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '10px',
                        color: '#7c3aed',
                        fontWeight: 'bold',
                        background: 'rgba(124, 58, 237, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        Global
                      </span>
                    )}
                    {modifiedSections[section.id] && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '10px',
                        color: '#f59e0b',
                        fontWeight: 'bold'
                      }}>
                        ● Modified
                      </span>
                    )}
                    {section.is_visible === false && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        color: '#888',
                        fontWeight: 'normal'
                      }}>
                        (Hidden)
                      </span>
                    )}
                  </span>

                  {/* Hover Controls */}
                  {hoveredSection?.id === section.id && (
                    <div className="section-controls">
                      {/* Hide move/delete for global sections */}
                      {!(section.is_global || section._isGlobalHeader || section._isGlobalFooter) && (
                        <>
                          <button
                            className="section-control-btn"
                            title="Move Up"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveSection('up', section);
                            }}
                          >
                            <FiChevronUp />
                          </button>
                          <button
                            className="section-control-btn"
                            title="Move Down"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveSection('down', section);
                            }}
                          >
                            <FiChevronDown />
                          </button>
                        </>
                      )}
                      {!(section.is_global || section._isGlobalHeader || section._isGlobalFooter) && (
                      <button
                        className="section-control-btn regenerate"
                        title="Regenerate with AI"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRegenPrompt(section);
                        }}
                        disabled={regeneratingSection === section.id}
                      >
                        <FiZap className={regeneratingSection === section.id ? 'spinning' : ''} />
                      </button>
                      )}
                      <button
                        className="section-control-btn"
                        title={section.is_visible ? "Hide" : "Show"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(section);
                        }}
                      >
                        {section.is_visible ? <FiEyeOff /> : <FiEye />}
                      </button>
                      {/* Hide delete for global sections */}
                      {!(section.is_global || section._isGlobalHeader || section._isGlobalFooter) && (
                        <button
                          className="section-control-btn danger"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section);
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Section Content - Rendered from content_json */}
                  <div className="we-section-content">
                    {renderWireframeContent(section)}
                  </div>
                </div>
              ));
              })()}
            </div>
          </div>

          {/* Zoom Controls */}
          <div className="canvas-controls">
            <button className="zoom-btn" onClick={handleZoomOut}>
              <FiZoomOut />
            </button>
            <span className="zoom-level">{zoom}%</span>
            <button className="zoom-btn" onClick={handleZoomIn}>
              <FiZoomIn />
            </button>
            <div className="toolbar-divider"></div>
            <button className="zoom-btn" onClick={handleFitScreen}>
              <FiMaximize2 />
            </button>
          </div>
        </main>

      </div>

      {/* Floatable Style Guide Panel */}
      {showStyleGuide && (
        <div
          className="style-guide-float"
          style={{
            left: `${styleGuidePosition.x}px`,
            top: `${styleGuidePosition.y}px`,
          }}
        >
          <div
            className="float-header"
            onMouseDown={handleDragStart}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <h3>Style Guide</h3>
            <button className="float-close" onClick={() => setShowStyleGuide(false)}>
              <FiX />
            </button>
          </div>

          <div className="float-content">
            {/* AI Suggestion Button */}
            <button
              className="ai-style-btn"
              onClick={() => { setShowAiStyleModal(true); setAiStyleSuggestions([]); }}
            >
              <FiZap size={14} />
              <span>AI Color Suggestions</span>
            </button>

            {/* Colors */}
            <div className="property-group">
              <div className="property-group-title">COLORS</div>
              {Object.entries(currentStyle.colors).map(([key, value]) => (
                <div className="color-row" key={key}>
                  <input
                    type="color"
                    className="color-swatch"
                    value={value}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                  />
                  <span className="color-label">
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                  <span className="color-value">{value}</span>
                  <button
                    className="reset-btn"
                    onClick={() => handleResetToDefault('color', key)}
                    title="Reset to default"
                  >
                    <FiRefreshCw />
                  </button>
                </div>
              ))}
            </div>

            {/* Typography */}
            <div className="property-group">
              <div className="property-group-title">TYPOGRAPHY</div>

              <div className="property-row">
                <label className="property-label">Heading Font</label>
                <button
                  className="reset-btn"
                  onClick={() => handleResetToDefault('font', 'heading')}
                  title="Reset to default"
                >
                  <FiRefreshCw />
                </button>
              </div>
              <select
                className="property-select"
                value={currentStyle.fonts.heading}
                onChange={(e) => handleFontChange("heading", e.target.value)}
              >
                <option value="Outfit">Outfit</option>
                <option value="Poppins">Poppins</option>
                <option value="Inter">Inter</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Playfair Display">Playfair Display</option>
              </select>

              <div className="property-row">
                <label className="property-label">Body Font</label>
                <button
                  className="reset-btn"
                  onClick={() => handleResetToDefault('font', 'body')}
                  title="Reset to default"
                >
                  <FiRefreshCw />
                </button>
              </div>
              <select
                className="property-select"
                value={currentStyle.fonts.body}
                onChange={(e) => handleFontChange("body", e.target.value)}
              >
                <option value="DM Sans">DM Sans</option>
                <option value="Inter">Inter</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Roboto">Roboto</option>
                <option value="Lato">Lato</option>
              </select>
            </div>

            {/* UI Elements */}
            <div className="property-group">
              <div className="property-group-title">UI ELEMENTS</div>

              <div className="property-row">
                <label className="property-label">Button Radius</label>
                <button
                  className="reset-btn"
                  onClick={() => handleResetToDefault('button', 'borderRadius')}
                  title="Reset to default"
                >
                  <FiRefreshCw />
                </button>
              </div>
              <select
                className="property-select"
                value={currentStyle.buttons.borderRadius}
                onChange={(e) => handleButtonChange("borderRadius", e.target.value)}
              >
                <option value="0px">Square (0px)</option>
                <option value="4px">Slight (4px)</option>
                <option value="8px">Medium (8px)</option>
                <option value="10px">Rounded (10px)</option>
                <option value="12px">Large (12px)</option>
                <option value="50px">Pill (50px)</option>
                <option value="9999px">Full Pill</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Autoresponder Floating Panel */}
      {showSeoPanel && (
        <div
          className="style-guide-float seo-panel"
          style={{ left: `${seoPosition.x}px`, top: `${seoPosition.y}px` }}
        >
          <div
            className="float-header"
            onMouseDown={handleSeoDragStart}
            style={{ cursor: seoDragging ? 'grabbing' : 'grab' }}
          >
            <h3>SEO</h3>
            <button className="float-close" onClick={() => setShowSeoPanel(false)}>
              <FiX />
            </button>
          </div>

          <div className="float-content">
            <div className="property-group-title">META TAGS</div>

            <div className="seo-field">
              <div className="seo-field-label">
                <span>Site Title</span>
                <span className={`seo-char-count${seoSettings.site_title.length > 55 ? ' warning' : ''}`}>
                  {seoSettings.site_title.length}/60
                </span>
              </div>
              <input
                type="text"
                className="seo-input"
                placeholder="My Website"
                maxLength={60}
                value={seoSettings.site_title}
                onChange={(e) => setSeoSettings({ ...seoSettings, site_title: e.target.value })}
              />
            </div>

            <div className="seo-field">
              <div className="seo-field-label">
                <span>Meta Description</span>
                <span className={`seo-char-count${seoSettings.meta_description.length > 155 ? ' danger' : seoSettings.meta_description.length > 140 ? ' warning' : ''}`}>
                  {seoSettings.meta_description.length}/160
                </span>
              </div>
              <textarea
                className="seo-input"
                placeholder="A brief description of your website for search engines..."
                rows={3}
                maxLength={160}
                value={seoSettings.meta_description}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_description: e.target.value })}
              />
            </div>

            <div className="seo-field">
              <div className="seo-field-label">
                <span>Meta Keywords</span>
              </div>
              <input
                type="text"
                className="seo-input"
                placeholder="keyword1, keyword2, keyword3"
                value={seoSettings.meta_keywords}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_keywords: e.target.value })}
              />
            </div>

            <div className="seo-divider" />

            <div className="property-group-title">FAVICON</div>

            <div className="seo-field">
              <div className="seo-favicon-row">
                <div className="seo-favicon-preview">
                  {seoSettings.favicon_url ? (
                    <img
                      src={seoSettings.favicon_url}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <FiGlobe size={16} style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>
                <button
                  className="seo-favicon-upload-btn"
                  onClick={() => setShowFaviconModal(true)}
                >
                  <FiUpload size={14} /> {seoSettings.favicon_url ? 'Change' : 'Upload'}
                </button>
                {seoSettings.favicon_url && (
                  <button
                    className="seo-favicon-remove-btn"
                    onClick={() => setSeoSettings({ ...seoSettings, favicon_url: '' })}
                  >
                    <FiTrash2 size={14} />
                  </button>
                )}
              </div>
            </div>

            <button
              className="seo-save-btn"
              onClick={saveSeoSettings}
              disabled={seoSaving || !seoSettings.site_title}
            >
              {seoSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {showAutoresponder && (
        <div
          className="style-guide-float"
          style={{ left: `${arPosition.x}px`, top: `${arPosition.y}px` }}
        >
          <div
            className="float-header"
            onMouseDown={handleArDragStart}
            style={{ cursor: arDragging ? 'grabbing' : 'grab' }}
          >
            <h3>Autoresponder</h3>
            <button className="float-close" onClick={() => setShowAutoresponder(false)}>
              <FiX />
            </button>
          </div>

          <div className="float-content">
            {arProviders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 10px', color: '#6b7280', fontSize: '13px' }}>
                <FiMail size={28} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.4 }} />
                <p style={{ margin: '0 0 8px' }}>No autoresponders connected.</p>
                <Link to="/settings" style={{ color: '#2563eb', fontSize: '13px' }}>
                  Go to Settings to connect one
                </Link>
              </div>
            ) : (
              <div className="property-group">
                <div className="property-group-title">LEAD CAPTURE</div>

                <label className="property-label" style={{ marginBottom: '4px' }}>Email Provider</label>
                <select
                  className="property-select"
                  value={arConfig.provider}
                  onChange={(e) => handleArProviderChange(e.target.value)}
                >
                  <option value="">— None —</option>
                  {arProviders.map((p) => (
                    <option key={p.meta_key} value={p.meta_key}>
                      {providerLabels[p.meta_key] || p.meta_key}
                    </option>
                  ))}
                </select>

                {arConfig.provider && (
                  <>
                    <label className="property-label" style={{ marginTop: '12px', marginBottom: '4px' }}>
                      {listFieldLabel(arConfig.provider)}
                    </label>
                    {arLoadingLists ? (
                      <div style={{ fontSize: '13px', color: '#6b7280', padding: '8px 0' }}>Loading...</div>
                    ) : (
                      <select
                        className="property-select"
                        value={arConfig.list_id}
                        onChange={(e) => handleArListChange(e.target.value)}
                      >
                        <option value="">— Select —</option>
                        {arLists.map((l) => (
                          <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                      </select>
                    )}
                  </>
                )}

                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                  <button
                    className="toolbar-btn"
                    style={{ flex: 1, justifyContent: 'center', fontSize: '13px', padding: '6px 12px' }}
                    onClick={saveArConfig}
                    disabled={arSaving || !arConfig.provider || !arConfig.list_id}
                  >
                    {arSaving ? 'Saving...' : 'Save'}
                  </button>
                  {arConfig.provider && (
                    <button
                      className="toolbar-btn"
                      style={{ fontSize: '13px', padding: '6px 12px', color: '#dc2626' }}
                      onClick={removeArConfig}
                      disabled={arSaving}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {arConfig.provider && arConfig.list_name && (
                  <div style={{ marginTop: '12px', padding: '8px 10px', backgroundColor: '#f0fdf4', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                    Active: {providerLabels[arConfig.provider]} → {arConfig.list_name}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Code Floating Panel */}
      {showCustomCodePanel && (
        <div
          className="style-guide-float custom-code-panel"
          style={{ left: `${customCodePosition.x}px`, top: `${customCodePosition.y}px` }}
        >
          <div
            className="float-header"
            onMouseDown={handleCustomCodeDragStart}
            style={{ cursor: customCodeDragging ? 'grabbing' : 'grab' }}
          >
            <h3>Custom Code</h3>
            <button className="float-close" onClick={() => setShowCustomCodePanel(false)}>
              <FiX />
            </button>
          </div>

          <div className="float-content">
            <div className="custom-code-tabs">
              <button
                className={`custom-code-tab ${customCodeTab === 'css' ? 'active' : ''}`}
                onClick={() => setCustomCodeTab('css')}
              >
                CSS
              </button>
              <button
                className={`custom-code-tab ${customCodeTab === 'script' ? 'active' : ''}`}
                onClick={() => setCustomCodeTab('script')}
              >
                JavaScript
              </button>
            </div>

            <div className="custom-code-notice">
              This code will only run on your published site, not in the editor.
            </div>

            {customCodeTab === 'css' ? (
              <textarea
                className="custom-code-editor"
                placeholder={"/* Add your custom CSS here */\nbody {\n  \n}"}
                rows={15}
                value={customCodeData.custom_css}
                onChange={(e) => setCustomCodeData({ ...customCodeData, custom_css: e.target.value })}
                spellCheck={false}
              />
            ) : (
              <textarea
                className="custom-code-editor"
                placeholder={"<script>\n// Add your custom JavaScript here\nconsole.log('Hello');\n</script>"}
                rows={15}
                value={customCodeData.custom_script}
                onChange={(e) => setCustomCodeData({ ...customCodeData, custom_script: e.target.value })}
                spellCheck={false}
              />
            )}

            <button
              className="seo-save-btn"
              onClick={saveCustomCode}
              disabled={customCodeSaving}
            >
              {customCodeSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* CNAME / Custom Domain Floating Panel */}
      {showCnamePanel && (
        <div
          className="style-guide-float cname-panel"
          style={{ left: `${cnamePosition.x}px`, top: `${cnamePosition.y}px` }}
        >
          <div
            className="float-header"
            onMouseDown={handleCnameDragStart}
            style={{ cursor: cnameDragging ? 'grabbing' : 'grab' }}
          >
            <h3>Custom Domain</h3>
            <button className="float-close" onClick={() => setShowCnamePanel(false)}>
              <FiX />
            </button>
          </div>

          <div className="float-content">
            {cnameData.project_status !== 'published' || !cnameData.subdomain ? (
              <div className="cname-not-published">
                <FiGlobe size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                <p>Publish your site first to add a custom domain.</p>
              </div>
            ) : cnameData.custom_domain ? (
              <div>
                <div className="cname-current-domain">
                  <FiCheckCircle size={16} style={{ color: '#10b981' }} />
                  <span>{cnameData.custom_domain}</span>
                </div>
                <a
                  href={`https://${cnameData.custom_domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cname-visit-link"
                >
                  Visit https://{cnameData.custom_domain}
                </a>
                <button
                  className="cname-remove-btn"
                  onClick={removeCnameConfig}
                  disabled={cnameRemoving}
                >
                  {cnameRemoving ? 'Removing...' : 'Remove Domain'}
                </button>
              </div>
            ) : (
              <div>
                <div className="cname-dns-instructions">
                  <div className="cname-dns-title">DNS Setup Instructions</div>
                  <p>Add a CNAME record with your DNS provider:</p>
                  <table className="cname-dns-table">
                    <tbody>
                      <tr><td>Type</td><td><strong>CNAME</strong></td></tr>
                      <tr><td>Name / Host</td><td><strong>www</strong> (or your subdomain)</td></tr>
                      <tr><td>Value / Target</td><td><strong>{cnameData.subdomain}.{config.baseDomain}</strong></td></tr>
                    </tbody>
                  </table>
                  <p className="cname-dns-note">DNS changes can take up to 48 hours to propagate.</p>
                </div>

                <div className="cname-input-group">
                  <label className="cname-input-label">Your Domain</label>
                  <div className="cname-input-wrapper">
                    <FiLink size={14} className="cname-input-icon" />
                    <input
                      type="text"
                      className="cname-input"
                      placeholder="www.yourdomain.com"
                      value={cnameInput}
                      onChange={(e) => setCnameInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && cnameInput.trim()) saveCnameConfig(); }}
                    />
                  </div>
                </div>

                <button
                  className="cname-save-btn"
                  onClick={saveCnameConfig}
                  disabled={cnameSaving || !cnameInput.trim()}
                >
                  {cnameSaving ? (
                    <><FiRefreshCw size={14} className="cname-spin" /> Verifying DNS...</>
                  ) : (
                    <><FiCheckCircle size={14} /> Verify & Connect</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Style Suggestion Modal */}
      {showAiStyleModal && (
        <div className="modal-overlay" onClick={() => setShowAiStyleModal(false)}>
          <div className="ai-style-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: "1px solid #e5e7eb", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h4 style={{ margin: 0, fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                <FiZap style={{ color: "#8b5cf6" }} /> AI Style Suggestions
              </h4>
              <button className="modal-close" onClick={() => setShowAiStyleModal(false)}><FiX /></button>
            </div>

            <div style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto" }}>
              {/* Mood Selection */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>Mood / Vibe</label>
                <select
                  value={aiStyleMood}
                  onChange={(e) => setAiStyleMood(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px" }}
                >
                  <option value="Modern and clean">Modern & Clean</option>
                  <option value="Warm and inviting">Warm & Inviting</option>
                  <option value="Bold and energetic">Bold & Energetic</option>
                  <option value="Elegant and luxurious">Elegant & Luxurious</option>
                  <option value="Playful and colorful">Playful & Colorful</option>
                  <option value="Minimal and monochrome">Minimal & Monochrome</option>
                  <option value="Dark and sleek">Dark & Sleek</option>
                  <option value="Nature and organic">Nature & Organic</option>
                </select>
              </div>

              {/* Custom Instructions */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>Custom Instructions (optional)</label>
                <input
                  type="text"
                  value={aiStyleCustom}
                  onChange={(e) => setAiStyleCustom(e.target.value)}
                  placeholder="e.g., Use blue tones, match competitor site..."
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px" }}
                />
              </div>

              {/* Reference Image */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "13px", fontWeight: "600", display: "block", marginBottom: "6px" }}>Reference Image (optional)</label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <label
                    style={{
                      padding: "7px 14px", borderRadius: "6px", border: "1px dashed #d1d5db",
                      fontSize: "12px", cursor: aiStyleImageUploading ? "default" : "pointer",
                      color: "#6b7280", background: "#f9fafb",
                      opacity: aiStyleImageUploading ? 0.6 : 1,
                    }}
                  >
                    {aiStyleImageUploading ? (
                      <><span className="spinner-border spinner-border-sm me-1" style={{ width: "12px", height: "12px" }} /> Uploading...</>
                    ) : "Upload Image"}
                    <input type="file" accept="image/*" onChange={handleAiStyleImageUpload} style={{ display: "none" }} disabled={aiStyleImageUploading} />
                  </label>
                  {aiStyleImageUrl && !aiStyleImageUploading && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <img src={aiStyleImageUrl} alt="ref" style={{ width: "36px", height: "36px", borderRadius: "6px", objectFit: "cover" }} />
                      <button onClick={() => setAiStyleImageUrl("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: "14px" }}>
                        <FiX />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateAiStyle}
                disabled={aiStyleLoading}
                style={{
                  width: "100%", padding: "10px", borderRadius: "8px", border: "none",
                  background: aiStyleLoading ? "#9ca3af" : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                  color: "#fff", fontSize: "14px", fontWeight: "600", cursor: aiStyleLoading ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
              >
                {aiStyleLoading ? (
                  <><span className="spinner-border spinner-border-sm" /> Generating...</>
                ) : (
                  <><FiZap size={16} /> Generate Suggestions</>
                )}
              </button>

              {/* Suggestions */}
              {aiStyleSuggestions.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "12px", color: "#374151" }}>
                    Choose a scheme:
                  </div>
                  {aiStyleSuggestions.map((scheme, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #e5e7eb", borderRadius: "10px", padding: "14px",
                        marginBottom: "12px", cursor: "pointer", transition: "all 0.15s",
                        background: "#fff",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#8b5cf6"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(139,92,246,0.15)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <span style={{ fontSize: "14px", fontWeight: "600" }}>{scheme.name}</span>
                        <button
                          onClick={() => handleApplyAiScheme(scheme)}
                          style={{
                            padding: "4px 12px", borderRadius: "6px", border: "1px solid #8b5cf6",
                            background: "#f5f3ff", color: "#7c3aed", fontSize: "12px", fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Apply
                        </button>
                      </div>

                      {/* Color Swatches */}
                      <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
                        {Object.entries(scheme.colors).map(([key, color]) => (
                          <div key={key} style={{ textAlign: "center" }}>
                            <div
                              style={{
                                width: "40px", height: "40px", borderRadius: "8px",
                                backgroundColor: color, border: "1px solid #e5e7eb",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              }}
                            />
                            <div style={{ fontSize: "9px", color: "#9ca3af", marginTop: "3px", textTransform: "capitalize" }}>
                              {key}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Fonts & Radius */}
                      <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "#6b7280" }}>
                        <span>Heading: <strong>{scheme.fonts?.heading}</strong></span>
                        <span>Body: <strong>{scheme.fonts?.body}</strong></span>
                        <span>Radius: <strong>{scheme.buttons?.borderRadius}</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Section Modal */}
      {editingSection && editingContent && (
        <div className="modal-overlay" onClick={() => { setEditingSection(null); setEditingContent(null); }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", maxHeight: "80vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h4>Edit {editingSection.component_code}</h4>
              <button className="modal-close" onClick={() => { setEditingSection(null); setEditingContent(null); }}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {/* Common fields for most sections */}
              {(editingSection.component_code.includes("HERO") ||
                editingSection.component_code.includes("ABOUT") ||
                editingSection.component_code.includes("SERVICES") ||
                editingSection.component_code.includes("FEATURES") ||
                editingSection.component_code.includes("CTA") ||
                editingSection.component_code.includes("TESTIMONIAL")) && (
                <>
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      type="text"
                      className="form-control-dark"
                      value={editingContent.heading || ""}
                      onChange={(e) => handleContentChange("heading", e.target.value)}
                      placeholder="Enter heading"
                    />
                  </div>
                  {!editingSection.component_code.includes("TESTIMONIAL") && (
                    <div className="form-group">
                      <label>Description / Subheading</label>
                      <textarea
                        className="form-control-dark"
                        rows="3"
                        value={editingContent.description || editingContent.subheading || ""}
                        onChange={(e) => handleContentChange("description", e.target.value)}
                        placeholder="Enter description"
                      />
                    </div>
                  )}
                </>
              )}

              {/* HEADER fields */}
              {editingSection.component_code.includes("HEADER") && (
                <>
                  <div className="form-group">
                    <label>Logo / Brand Name</label>
                    <input
                      type="text"
                      className="form-control-dark"
                      value={editingContent.logo || editingContent.brand || ""}
                      onChange={(e) => handleContentChange("logo", e.target.value)}
                      placeholder="Brand Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Navigation Items (comma-separated)</label>
                    <input
                      type="text"
                      className="form-control-dark"
                      value={(editingContent.nav_items || []).join(", ")}
                      onChange={(e) => handleContentChange("nav_items", e.target.value.split(",").map(s => s.trim()))}
                      placeholder="Home, About, Services, Contact"
                    />
                  </div>
                </>
              )}

              {/* CTA Button */}
              {(editingSection.component_code.includes("HERO") || editingSection.component_code.includes("CTA")) && (
                <div className="form-group">
                  <label>Button Text</label>
                  <input
                    type="text"
                    className="form-control-dark"
                    value={editingContent.cta_text || ""}
                    onChange={(e) => handleContentChange("cta_text", e.target.value)}
                    placeholder="Get Started"
                  />
                </div>
              )}

              {/* FOOTER fields */}
              {editingSection.component_code.includes("FOOTER") && (
                <>
                  <div className="form-group">
                    <label>Brand Name</label>
                    <input
                      type="text"
                      className="form-control-dark"
                      value={editingContent.brand || editingContent.logo || ""}
                      onChange={(e) => handleContentChange("brand", e.target.value)}
                      placeholder="Brand Name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Footer Description</label>
                    <textarea
                      className="form-control-dark"
                      rows="2"
                      value={editingContent.description || ""}
                      onChange={(e) => handleContentChange("description", e.target.value)}
                      placeholder="Building the future..."
                    />
                  </div>
                </>
              )}

              <div className="form-group" style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #374151" }}>
                <small className="text-muted" style={{ fontSize: "12px" }}>
                  Component: <strong>{editingSection.component_code}</strong>
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setEditingSection(null); setEditingContent(null); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveContent}>
                <FiSave className="me-1" /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link/Button Edit Popup */}
      {linkPopup.show && (
        <div
          className="link-edit-popup"
          style={{
            position: 'fixed',
            left: `${linkPopup.position.x}px`,
            top: `${linkPopup.position.y}px`,
            transform: 'translateX(-50%)',
            zIndex: 10000,
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            minWidth: '280px',
            border: '1px solid #374151',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Arrow pointer */}
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '8px solid #374151',
            }}
          />

          {linkPopup.isIconOnly ? (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Icon
              </label>
              <button
                onClick={() => {
                  // Open icon picker for this icon, then close link popup
                  if (linkPopup.iconElement) {
                    const iconClass = Array.from(linkPopup.iconElement.classList).find(c => c.startsWith('bi-') && c !== 'bi');
                    const currentIcon = iconClass ? iconClass.replace('bi-', '') : '';
                    setIconPickerModal({ show: true, currentIcon, iconElement: linkPopup.iconElement, wireframeId: linkPopup.wireframeId, container: linkPopup.container });
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {linkPopup.iconElement && (
                  <i className={linkPopup.iconElement.className} style={{ fontSize: '18px' }}></i>
                )}
                <span>Change Icon</span>
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {linkPopup.element?.tagName === 'BUTTON' ? 'Button Text' : 'Link Text'}
              </label>
              <input
                type="text"
                value={linkPopup.text}
                onChange={(e) => setLinkPopup(prev => ({ ...prev, text: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                }}
                placeholder="Enter text..."
                autoFocus
              />
            </div>
          )}

          {linkPopup.element?.tagName === 'A' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Link Destination
              </label>
              <select
                value={linkPopup.linkType === 'custom' ? 'custom' : linkPopup.href}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setLinkPopup(prev => ({ ...prev, linkType: 'custom', href: '' }));
                  } else {
                    setLinkPopup(prev => ({ ...prev, linkType: 'page', href: value }));
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                  marginBottom: linkPopup.linkType === 'custom' ? '8px' : '0',
                }}
              >
                <option value="/" style={{ backgroundColor: '#374151' }}>/ (Home)</option>
                {getPageRoutes().filter(p => p.route !== '/').map(page => (
                  <option key={page.route} value={page.route} style={{ backgroundColor: '#374151' }}>
                    {page.route} ({page.name})
                  </option>
                ))}
                <option value="#" style={{ backgroundColor: '#374151' }}># (Same Page)</option>
                <option value="custom" style={{ backgroundColor: '#374151', borderTop: '1px solid #4b5563' }}>
                  Custom Link...
                </option>
              </select>

              {linkPopup.linkType === 'custom' && (
                <input
                  type="text"
                  value={linkPopup.href}
                  onChange={(e) => setLinkPopup(prev => ({ ...prev, href: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  placeholder="https://example.com or #section-id"
                />
              )}
              {linkPopup.linkType === 'custom' && (
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', marginBottom: '0' }}>
                  Enter full URL (https://...) or anchor link (#section)
                </p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCloseLinkPopup}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#9ca3af',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveLinkPopup}
              style={{
                padding: '8px 16px',
                backgroundColor: '#7c3aed',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Backdrop for link popup */}
      {linkPopup.show && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'transparent',
          }}
          onClick={handleCloseLinkPopup}
        />
      )}

      {/* Input Placeholder Edit Popup */}
      {placeholderPopup.show && (
        <div
          className="link-edit-popup"
          style={{
            position: 'fixed',
            left: `${placeholderPopup.position.x}px`,
            top: `${placeholderPopup.position.y}px`,
            transform: 'translateX(-50%)',
            zIndex: 10000,
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
            minWidth: '280px',
            border: '1px solid #374151',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Arrow pointer */}
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '8px solid #374151',
            }}
          />

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Placeholder Text
            </label>
            <input
              type="text"
              value={placeholderPopup.placeholder}
              onChange={(e) => setPlaceholderPopup(prev => ({ ...prev, placeholder: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSavePlaceholderPopup();
                if (e.key === 'Escape') handleClosePlaceholderPopup();
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                outline: 'none',
              }}
              placeholder="Enter placeholder text..."
              autoFocus
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleClosePlaceholderPopup}
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                color: '#9ca3af',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSavePlaceholderPopup}
              style={{
                padding: '8px 16px',
                backgroundColor: '#7c3aed',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Backdrop for placeholder popup */}
      {placeholderPopup.show && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'transparent',
          }}
          onClick={handleClosePlaceholderPopup}
        />
      )}

      {/* Image Upload Modal */}
      <ImageUploadModal
        show={imageModal.show}
        onClose={handleCloseImageModal}
        onImageSelect={handleImageSelect}
        currentImage={imageModal.currentSrc}
        suggestedKeywords={(() => {
          // Extract image_keywords from the wireframe's content_json
          if (!imageModal.wireframeId || !wireframes) return [];
          const wireframe = wireframes.find(w => w.id === imageModal.wireframeId);
          if (!wireframe?.content_json) return [];
          try {
            const content = JSON.parse(wireframe.content_json);
            return content.image_keywords || [];
          } catch (e) {
            return [];
          }
        })()}
        aiImagePrompt={(() => {
          // Find the matching AI image prompt for the clicked image
          if (!imageModal.wireframeId || !wireframes || !imageModal.currentSrc) return "";
          const wireframe = wireframes.find(w => w.id === imageModal.wireframeId);
          if (!wireframe?.content_json) return "";
          try {
            const content = JSON.parse(wireframe.content_json);
            const prompts = content.image_ai_prompts;
            if (!prompts || typeof prompts !== 'object') return "";
            // Match by comparing the clicked image src against content field values
            const imgSrc = imageModal.currentSrc;
            for (const [fieldName, fieldValue] of Object.entries(content)) {
              if (typeof fieldValue === 'string' && fieldValue === imgSrc && prompts[fieldName]) {
                return prompts[fieldName];
              }
            }
            // Fallback: if only one prompt exists, use it
            const promptValues = Object.values(prompts);
            if (promptValues.length === 1) return promptValues[0];
            // Fallback: for background images, try background_image field
            if (imageModal.isBackground && prompts.background_image) return prompts.background_image;
            return "";
          } catch (e) {
            return "";
          }
        })()}
      />

      {/* Favicon Upload Modal */}
      <ImageUploadModal
        show={showFaviconModal}
        onClose={() => setShowFaviconModal(false)}
        onImageSelect={(url) => {
          setSeoSettings({ ...seoSettings, favicon_url: url });
          setShowFaviconModal(false);
        }}
        currentImage={seoSettings.favicon_url}
        suggestedKeywords={['favicon', 'logo', 'icon', 'brand']}
      />

      {/* Map Edit Modal */}
      <MapEditModal
        show={mapModal.show}
        onClose={handleCloseMapModal}
        onMapUpdate={handleMapUpdate}
        currentAddress={mapModal.currentAddress}
      />

      {/* Icon Picker Modal */}
      <IconPickerModal
        show={iconPickerModal.show}
        currentIcon={iconPickerModal.currentIcon}
        onSelect={handleIconSelected}
        onClose={() => setIconPickerModal({ show: false, currentIcon: '', iconElement: null, wireframeId: null, container: null })}
      />

      {/* Publish Modal */}
      <PublishModal
        show={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        project={currentProject}
        onPublishSuccess={() => {
          setShowPublishModal(false);
          loadProject(); // Reload project to get updated status
        }}
      />

      {/* Leave Confirmation Modal */}
      <ConfirmModal
        show={showLeaveConfirm}
        title="Unsaved Changes"
        message={`You have ${Object.keys({ ...modifiedSections, ...modifiedSectionsRef.current }).length} unsaved section${Object.keys({ ...modifiedSections, ...modifiedSectionsRef.current }).length !== 1 ? 's' : ''}. Are you sure you want to leave? Your edits will be lost.`}
        confirmText="Leave Without Saving"
        cancelText="Stay & Save"
        variant="warning"
        onConfirm={() => { setShowLeaveConfirm(false); history.push('/'); }}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      {/* AI Regenerate Prompt Modal */}
      {regenPrompt.show && (
        <div className="regen-prompt-overlay" onClick={() => setRegenPrompt({ show: false, section: null, instructions: '' })}>
          <div className="regen-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="regen-prompt-header">
              <div className="regen-prompt-icon">
                <FiZap />
              </div>
              <div>
                <h6 className="mb-0">Regenerate with AI</h6>
                <span className="text-muted small">{regenPrompt.section?.component_code}</span>
              </div>
              <button
                className="regen-prompt-close"
                onClick={() => setRegenPrompt({ show: false, section: null, instructions: '' })}
              >
                <FiX />
              </button>
            </div>
            <div className="regen-prompt-body">
              <label className="form-label small fw-semibold mb-1">
                What would you like to change?
              </label>
              <textarea
                className="regen-prompt-textarea"
                rows={3}
                placeholder="e.g. Make the headline bolder, add more services, change the layout to 3 columns, use a dark background..."
                value={regenPrompt.instructions}
                onChange={(e) => setRegenPrompt(prev => ({ ...prev, instructions: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleRegenerateSection(regenPrompt.section, regenPrompt.instructions);
                  }
                }}
                autoFocus
              />
              <span className="regen-prompt-hint">Press Ctrl+Enter to submit</span>
            </div>
            <div className="regen-prompt-footer">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handleRegenerateSection(regenPrompt.section, '')}
              >
                <FiRefreshCw size={13} className="me-1" />
                Regenerate without instructions
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleRegenerateSection(regenPrompt.section, regenPrompt.instructions)}
                disabled={!regenPrompt.instructions.trim()}
              >
                <FiZap size={13} className="me-1" />
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WireframeEditor;
